package features.booking

import network.FirebaseClient
import kotlinx.coroutines.flow.first
import kotlin.collections.emptyList
import kotlin.time.Clock
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.delay
import features.booking.NotificacionViewModel
import io.ktor.client.request.post
import io.ktor.client.request.header
import io.ktor.client.request.setBody
import io.ktor.http.contentType
import io.ktor.http.ContentType
import dev.gitlive.firebase.auth.auth

interface BookingRepository {

    suspend fun bookSeats(boleto: Boleto): Boolean

    suspend fun getBookings(): List<Boleto>
    suspend fun getBookingsByPath(path: String): List<Boleto>
    suspend fun getViajesCompletos(path: String): Map<String, DatosDia>

    suspend fun getTodoElHistorial(path: String, fechaDesde: String = "", fechaHasta: String = ""): Map<String, Map<String, Map<String, DatosDia>>>

    suspend fun programarViaje(ruta: String, fecha: String, horario: String, chofer: String, vehiculo: String, puntoSubida: String = "", urlSalida: String = "", choferUid: String = ""): Boolean

    suspend fun getHorariosProgramados(ruta: String, fecha: String): List<String>
    suspend fun eliminarViaje(ruta: String, fecha: String, horario: String): Boolean

    suspend fun contarAsientosOcupados(ruta: String, fecha: String, horario: String): Int
    suspend fun obtenerTodasLasReservasAdmin(fechaHoy: String): List<ReservaAdmin>
    suspend fun obtenerListaAsientosOcupados(ruta: String, fecha: String, horario: String): List<Int>

    suspend fun getOccupiedSeats(ruta: String, fecha: String, horario: String): List<Int>
    fun observarAsientosNoDisponibles(ruta: String, fecha: String, horario: String, miIdSesion: String): Flow<List<Int>>
    suspend fun bloquearAsientoTemporal(ruta: String, fecha: String, horario: String, asiento: Int, miIdSesion: String): Long?
    suspend fun liberarAsientoBloqueado(ruta: String, fecha: String, horario: String, asiento: Int): Unit
    suspend fun obtenerMapaAsientosAdmin(ruta: String, fecha: String, horario: String): List<AdminSeatInfo>
    suspend fun obtenerHistorialPagos(): List<PagoRegistro>
    suspend fun obtenerConflictosAsientos(): List<ConflictoAsiento>
    suspend fun reasignarAsientoConflicto(conflictoId: String, asientoViejo: Int, asientoNuevo: Int, pasajero: PasajeroInfo): Boolean
    suspend fun marcarConflictoReembolsado(conflictoId: String): Boolean
    suspend fun reasignarLogisticaMasiva(choferSaliente: String, choferEntrante: String, vehiculoNuevo: String, aPartirDeFecha: String, choferEntranteUid: String = ""): Int

    suspend fun reprogramarReserva(ruta: String, fechaAntigua: String, horarioAntiguo: String, idReserva: String, nuevaFecha: String, nuevoHorario: String): Boolean

    suspend fun reprogramarReservaCompleta(rutaAntigua: String, fechaAntigua: String, horarioAntiguo: String, idReserva: String, rutaNueva: String, fechaNueva: String, horarioNuevo: String, nuevosAsientos: List<Int>): Boolean

    suspend fun dividirYReprogramarGrupo(rutaAntigua: String, fechaAntigua: String, horarioAntiguo: String, idReservaOriginal: String, pasajerosAMover: List<PasajeroInfo>, rutaNueva: String, fechaNueva: String, horarioNuevo: String, nuevosAsientos: List<Int>): Boolean

    suspend fun cancelarReservaAdmin(ruta: String, fecha: String, horario: String, idReserva: String): Boolean

    // ═══ Reasignación de cancelaciones ═══
    suspend fun obtenerViajesProgramadosDisponibles(): List<ViajeDisponibleReasignacion>
    /** Devuelve (capacidad, asientos libres) del viaje destino, con la numeración real del vehículo. */
    suspend fun obtenerAsientosLibresViaje(ruta: String, fecha: String, horario: String): Pair<Int, List<Int>>
    suspend fun reasignarReservaCancelada(cancelacionId: String, rutaNueva: String, fechaNueva: String, horarioNuevo: String, asientosElegidos: List<Int>? = null): ReasignacionResultado

    // ═══ Vehículos con layout ═══
    suspend fun guardarVehiculoConfig(vehiculo: VehiculoConfig): Boolean
    suspend fun editarVehiculoConfig(nombreViejo: String, vehiculo: VehiculoConfig): Boolean
    suspend fun obtenerVehiculosConfig(): List<VehiculoConfig>
    suspend fun obtenerVehiculoConfig(vehiculoId: String): VehiculoConfig?
    suspend fun eliminarVehiculoConfig(vehiculoId: String): Boolean
    suspend fun obtenerLayoutDeViaje(ruta: String, fecha: String, horario: String): VehiculoConfig?

}

    class BookingRepositoryImpl : BookingRepository {

        private val misSesionesActivas = mutableMapOf<String, String>() // Accessed from main/IO dispatchers only via coroutines

        override suspend fun programarViaje(ruta: String, fecha: String, horario: String, chofer: String, vehiculo: String, puntoSubida: String, urlSalida: String, choferUid: String): Boolean {
            return try {
                val db = network.FirebaseClient.database
                val ref = db.reference("viajes_programados/$ruta/$fecha/$horario")
                // Validar que no exista un viaje duplicado en la misma ruta/fecha/horario
                val existente = ref.valueEvents.first()
                if (existente.exists) {
                    println("WARN: Ya existe un viaje programado en $ruta / $fecha / $horario")
                    return false
                }
                val datos = mutableMapOf<String, Any>(
                    "chofer" to chofer,
                    "vehiculo" to vehiculo,
                    "estado" to "Activo"
                )
                // Vincular por uid (robusto): el match del chofer prioriza uid sobre nombre
                if (choferUid.isNotBlank()) {
                    datos["choferUid"] = choferUid
                }
                if (puntoSubida.isNotBlank()) {
                    datos["puntoSubida"] = puntoSubida
                }
                // urlSalida quedó obsoleto: el link de ubicación ahora sale de las coordenadas
                // de puntosSubidaInfo (mapsUrl). No se persiste.
                // Buscar vehiculoId por nombre para vincular layout
                val vehiculosSnap = db.reference("vehiculos_config").valueEvents.first()
                if (vehiculosSnap.exists) {
                    vehiculosSnap.children.forEach { vs ->
                        val nombre = try { vs.child("nombre").value<String>() } catch (e: Exception) { "" }
                        if (nombre == vehiculo) {
                            datos["vehiculoId"] = vs.key ?: ""
                            datos["capacidad"] = try { vs.child("capacidad").value<Int>() } catch (e: Exception) { 13 }
                        }
                    }
                }
                ref.setValue(datos)
                true
            } catch (e: Exception) {
                false
            }
        }

        override suspend fun getOccupiedSeats(ruta: String, fecha: String, horario: String): List<Int> {
            return try {
                val snapshot = network.FirebaseClient.database
                    .reference("viajes/$ruta/$fecha/$horario/ocupados")
                    .valueEvents.first()

                if (snapshot.exists) {
                    val lista = mutableListOf<Int>()
                    snapshot.children.forEach { child ->
                        val asiento = child.key?.toIntOrNull()
                        if (asiento != null) {
                            val valor = try { child.value<Boolean>() } catch (e: Exception) { false }
                            if (valor) lista.add(asiento)
                        }
                    }
                    lista
                } else emptyList()
            } catch (e: Exception) {
                emptyList()
            }
        }

        // NOTA: bookSeats() NO se usa actualmente en la app.
        // Las reservas con tarjeta van por Stripe webhook (con transaction() atómico).
        // Las reservas en efectivo van por Cloud Function ocuparAsientosEfectivo.
        // Se mantiene por compatibilidad de interfaz pero NO debe usarse para producción
        // ya que carece de transacciones atómicas en la ocupación de asientos.
        override suspend fun bookSeats(boleto: Boleto): Boolean {
            return try {
                val db = network.FirebaseClient.database
                val rutaBase = "viajes/${boleto.origenDestino}/${boleto.fechaViaje}/${boleto.horario}"

                val ocupadosSnap = db.reference("$rutaBase/ocupados").valueEvents.first()
                val ocupadosActuales: Set<Int> = if (ocupadosSnap.exists) {
                    try {
                        val raw = ocupadosSnap.value<Any?>()
                        when (raw) {
                            is Map<*, *> -> raw.keys.mapNotNull { (it as? String)?.toIntOrNull() }.toSet()
                            is List<*> -> raw.mapIndexedNotNull { idx, v -> if (v != null) idx else null }.toSet()
                            else -> emptySet()
                        }
                    } catch (e: Exception) { emptySet() }
                } else emptySet()

                val conflicto = boleto.asientos.any { it in ocupadosActuales }
                if (conflicto) {
                    return false
                }

                val updates = mutableMapOf<String, Any?>()
                boleto.asientos.forEach { asiento ->
                    updates["$rutaBase/ocupados/$asiento"] = true
                    updates["$rutaBase/bloqueos/$asiento"] = null
                }

                val boletoId = db.reference("$rutaBase/reservas").push().key
                    ?: "reserva_${boleto.timestamp}"
                updates["$rutaBase/reservas/$boletoId"] = boleto

                boleto.asientos.forEachIndexed { index, asiento ->
                    val codigoQR = "SMARTVAN-${boleto.timestamp}-$asiento"
                    updates["$rutaBase/verificaciones/$codigoQR"] = mapOf(
                        "asiento" to asiento,
                        "pasajero" to (boleto.pasajeros.getOrNull(index)?.nombre ?: ""),
                        "verificado" to false,
                        "reservaId" to boletoId
                    )
                }

                db.reference().updateChildren(updates)
                NotificacionViewModel.enviar(
                    tipo = "venta",
                    titulo = "Nueva venta realizada",
                    mensaje = "Asiento(s) ${boleto.asientos.joinToString(", ")} vendido(s) para ${boleto.origenDestino} el ${boleto.fechaViaje} a las ${boleto.horario}. Total: $${boleto.totalPagado} MXN",
                    destinatarioRol = "admin"
                )
                true
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "BookingRepositoryImpl.bookSeats[${boleto.origenDestino}]")
                false
            }
        }

        override suspend fun getBookings(): List<Boleto> {
            // Legacy: no se usa actualmente, retorna lista vacía
            return emptyList()
        }

        override suspend fun getBookingsByPath(path: String): List<Boleto> {
            return try {
                val db = FirebaseClient.database
                val snapshot = db.reference(path).valueEvents.first()

                if (snapshot.exists) {
                    val boletos = mutableListOf<Boleto>()
                    snapshot.children.forEach { reservaSnap ->
                        try {
                            boletos.add(reservaSnap.value<Boleto>())
                        } catch (e: Exception) {
                            try {
                                val totalPagado = try { reservaSnap.child("totalPagado").value<Int>() } catch (_: Exception) { 0 }
                                val fechaViaje = try { reservaSnap.child("fechaViaje").value<String>() } catch (_: Exception) { "" }
                                val horarioR = try { reservaSnap.child("horario").value<String>() } catch (_: Exception) { "" }
                                val origenDestino = try { reservaSnap.child("origenDestino").value<String>() } catch (_: Exception) { "" }
                                val timestamp = try { reservaSnap.child("timestamp").value<String>() } catch (_: Exception) { "0" }
                                val asientosList = mutableListOf<Int>()
                                val asientosSnap = reservaSnap.child("asientos")
                                if (asientosSnap.exists) {
                                    asientosSnap.children.forEach { a -> try { asientosList.add(a.value<Int>()) } catch (_: Exception) {} }
                                }
                                val pasajerosList = mutableListOf<PasajeroInfo>()
                                val pasajerosSnap = reservaSnap.child("pasajeros")
                                if (pasajerosSnap.exists) {
                                    pasajerosSnap.children.forEach { p -> try { pasajerosList.add(p.value<PasajeroInfo>()) } catch (_: Exception) {} }
                                }
                                boletos.add(Boleto(asientos = asientosList, pasajeros = pasajerosList, totalPagado = totalPagado, fechaViaje = fechaViaje, horario = horarioR, origenDestino = origenDestino, timestamp = timestamp))
                            } catch (_: Exception) {}
                        }
                    }
                    boletos
                } else {
                    emptyList()
                }
            } catch (e: Exception) {
                emptyList()
            }
        }

        override suspend fun getViajesCompletos(path: String): Map<String, DatosDia> {
            return try {
                val db = FirebaseClient.database
                val snapshot = db.reference(path).valueEvents.first()

                if (snapshot.exists) {
                    snapshot.value<Map<String, DatosDia>>()
                } else {
                    emptyMap()
                }
            } catch (e: Exception) {
                emptyMap()
            }
        }

        override suspend fun getTodoElHistorial(path: String, fechaDesde: String, fechaHasta: String): Map<String, Map<String, Map<String, DatosDia>>> {
            val resultado = mutableMapOf<String, Map<String, Map<String, DatosDia>>>()
            return try {
                val db = FirebaseClient.database
                val snapshot = db.reference(path).valueEvents.first()

                if (snapshot.exists) {
                    snapshot.children.forEach { rutaSnap ->
                        val nombreRuta = rutaSnap.key ?: return@forEach
                        val diasMap = mutableMapOf<String, Map<String, DatosDia>>()

                        rutaSnap.children.forEach { fechaSnap ->
                            val fecha = fechaSnap.key ?: return@forEach

                            // FIX #11: Skip early — no parsear reservas de fechas fuera del rango
                            if (fechaDesde.isNotEmpty() && fecha < fechaDesde) return@forEach
                            if (fechaHasta.isNotEmpty() && fecha > fechaHasta) return@forEach

                            val horariosMap = mutableMapOf<String, DatosDia>()

                            fechaSnap.children.forEach { horarioSnap ->
                                val horario = horarioSnap.key ?: return@forEach
                                // Solo procesar nodos que son horarios (tienen reservas)
                                val reservasSnap = horarioSnap.child("reservas")
                                if (reservasSnap.exists) {
                                    val reservasMap = mutableMapOf<String, Boleto>()
                                    reservasSnap.children.forEach { reservaSnap ->
                                        val key = reservaSnap.key ?: return@forEach
                                        try {
                                            val boleto = reservaSnap.value<Boleto>()
                                            reservasMap[key] = boleto
                                        } catch (e: Exception) {
                                            // Parseo manual como fallback
                                            try {
                                                val totalPagado = try { reservaSnap.child("totalPagado").value<Int>() } catch (_: Exception) { 0 }
                                                val fechaViaje = try { reservaSnap.child("fechaViaje").value<String>() } catch (_: Exception) { "" }
                                                val horarioR = try { reservaSnap.child("horario").value<String>() } catch (_: Exception) { "" }
                                                val origenDestino = try { reservaSnap.child("origenDestino").value<String>() } catch (_: Exception) { "" }
                                                val timestamp = try { reservaSnap.child("timestamp").value<String>() } catch (_: Exception) { "0" }

                                                val asientosList = mutableListOf<Int>()
                                                val asientosSnap = reservaSnap.child("asientos")
                                                if (asientosSnap.exists) {
                                                    asientosSnap.children.forEach { a ->
                                                        try { asientosList.add(a.value<Int>()) } catch (_: Exception) {}
                                                    }
                                                }

                                                val pasajerosList = mutableListOf<PasajeroInfo>()
                                                val pasajerosSnap = reservaSnap.child("pasajeros")
                                                if (pasajerosSnap.exists) {
                                                    pasajerosSnap.children.forEach { p ->
                                                        try { pasajerosList.add(p.value<PasajeroInfo>()) } catch (_: Exception) {}
                                                    }
                                                }

                                                reservasMap[key] = Boleto(
                                                    asientos = asientosList,
                                                    pasajeros = pasajerosList,
                                                    totalPagado = totalPagado,
                                                    fechaViaje = fechaViaje,
                                                    horario = horarioR,
                                                    origenDestino = origenDestino,
                                                    timestamp = timestamp
                                                )
                                            } catch (e2: Exception) {
                                            }
                                        }
                                    }
                                    if (reservasMap.isNotEmpty()) {
                                        horariosMap[horario] = DatosDia(reservas = reservasMap)
                                    }
                                }
                            }

                            if (horariosMap.isNotEmpty()) {
                                diasMap[fecha] = horariosMap
                            }
                        }

                        if (diasMap.isNotEmpty()) {
                            resultado[nombreRuta] = diasMap
                        }
                    }
                }
                resultado
            } catch (e: Exception) {
                emptyMap()
            }
        }

        override suspend fun getHorariosProgramados(ruta: String, fecha: String): List<String> {
            return try {
                val db = network.FirebaseClient.database
                val snapshot = db.reference("viajes_programados/$ruta/$fecha").valueEvents.first()

                if (!snapshot.exists) return emptyList()

                val horarios = snapshot.children.mapNotNull { child ->
                    child.key
                }
                horarios.sorted()
            } catch (e: Exception) {
                emptyList()
            }
        }

        override suspend fun eliminarViaje(ruta: String, fecha: String, horario: String): Boolean {
            return try {
                network.FirebaseClient.database.reference("viajes_programados/$ruta/$fecha/$horario")
                    .removeValue()
                true
            } catch (e: Exception) {
                false
            }
        }

        override suspend fun contarAsientosOcupados(ruta: String, fecha: String, horario: String): Int {
            return try {
                val path = "viajes/$ruta/$fecha/$horario/reservas"
                val snapshot = network.FirebaseClient.database.reference(path).valueEvents.first()
                var totalAsientosVendidos = 0

                if (snapshot.exists) {
                    snapshot.children.forEach { reservaSnap ->
                        try {
                            val asientosSnap = reservaSnap.child("asientos")

                            if (asientosSnap.exists) {
                                totalAsientosVendidos += asientosSnap.children.count()
                            }
                        } catch (e: Exception) {
                        }
                    }
                }

                totalAsientosVendidos

            } catch (e: Exception) {
                0
            }
        }

        // FIX #3 v2: Iterar ruta por ruta leyendo solo fechas dentro de la ventana.
        // Ya NO descarga "viajes" entero de golpe — lee solo lo necesario.
        override suspend fun obtenerTodasLasReservasAdmin(fechaHoy: String): List<ReservaAdmin> {
            val listaReservas = mutableListOf<ReservaAdmin>()
            return try {
                val db = network.FirebaseClient.database

                // Calcular ventana de fechas: 30 días atrás hasta 60 días adelante
                val fechaMinima = org.example.abigail.calcularFechaFutura(fechaHoy, -30)
                val fechaMaxima = org.example.abigail.calcularFechaFutura(fechaHoy, 60)

                // Paso 1: Obtener nombres de rutas (shallow — solo keys, no datos anidados)
                val rutasSnap = db.reference("viajes").valueEvents.first()
                if (!rutasSnap.exists) return emptyList()
                val nombresRutas = rutasSnap.children.mapNotNull { it.key }

                // Paso 2: Leer viajes_programados una sola vez para verificar cancelaciones
                val viajesProgramadosSnap = try {
                    db.reference("viajes_programados").valueEvents.first()
                } catch (e: Exception) { null }

                // Paso 3: Iterar ruta por ruta, fecha por fecha (solo dentro de ventana)
                // Las llaves de fecha ("YYYY-MM-DD") ordenan cronológicamente, así que filtramos
                // la ventana en el servidor con orderByKey().startAt().endAt() en vez de traer todo.
                for (nombreRuta in nombresRutas) {
                    val rutaSnap = try {
                        db.reference("viajes/$nombreRuta")
                            .orderByKey()
                            .startAt(fechaMinima)
                            .endAt(fechaMaxima)
                            .valueEvents.first()
                    } catch (e: Exception) { continue }
                    if (!rutaSnap.exists) continue

                    rutaSnap.children.forEach { fechaSnap ->
                        val fecha = fechaSnap.key ?: return@forEach
                        // Defensa extra (la query ya acota), por si la llave no es una fecha
                        if (fecha < fechaMinima || fecha > fechaMaxima) return@forEach

                        fechaSnap.children.forEach { horarioSnap ->
                            val horario = horarioSnap.key ?: return@forEach
                            // Skip nodos que no son horarios
                            if (horario == "ocupados" || horario == "bloqueos" || horario == "verificaciones" || horario == "asistencia" || horario == "rastreo") return@forEach

                            // Un viaje de HOY que ya salió es pasado. Antes solo se comparaba el
                            // día, así que un viaje de la mañana seguía "vigente" toda la tarde.
                            val esViajePasado = fecha < fechaHoy ||
                                (fecha == fechaHoy && horaAMinutos(horario) < org.example.abigail.obtenerMinutosActuales())

                            val reservasSnap = horarioSnap.child("reservas")
                            if (reservasSnap.exists) {
                                reservasSnap.children.forEach { reservaSnap ->
                                    try {
                                        val idReserva = reservaSnap.key ?: ""
                                        val horarioReserva = try {
                                            reservaSnap.child("horario").value<String>()
                                        } catch (e: Exception) { horario }

                                        val pagado = try {
                                            reservaSnap.child("totalPagado").value<Int>()
                                        } catch (e: Exception) { 0 }

                                        val asientosReales = try {
                                            reservaSnap.child("asientos").children.mapNotNull { child ->
                                                try { child.value<Int>() } catch (e: Exception) { null }
                                            }
                                        } catch (e: Exception) { emptyList() }

                                        val totalAsientos = asientosReales.size

                                        val sigueProgramado = viajesProgramadosSnap
                                            ?.child(nombreRuta)?.child(fecha)?.child(horarioReserva ?: horario)?.exists ?: false
                                        val estaCancelado = !esViajePasado && !sigueProgramado

                                        val listaPasajeros = mutableListOf<PasajeroInfo>()
                                        try {
                                            reservaSnap.child("pasajeros").children.forEach { pSnap ->
                                                try { listaPasajeros.add(PasajeroInfo.fromSnapshot(pSnap)) } catch (e: Exception) {}
                                            }
                                        } catch (e: Exception) {}

                                        if (listaPasajeros.isEmpty() && asientosReales.isNotEmpty()) {
                                            asientosReales.forEachIndexed { index, numeroReal ->
                                                listaPasajeros.add(
                                                    PasajeroInfo(
                                                        nombre = "Pasajero ${index + 1}",
                                                        asiento = numeroReal,
                                                        telefono = ""
                                                    )
                                                )
                                            }
                                        }

                                        val metodoPagoVal = try { reservaSnap.child("metodoPago").value<String>() } catch (e: Exception) { "" }
                                        val pagoConfirmadoVal = try { reservaSnap.child("pagoConfirmado").value<Boolean>() } catch (e: Exception) { true }
                                        val puntoSubidaVal = try { reservaSnap.child("puntoSubida").value<String>() } catch (e: Exception) { "" }

                                        listaReservas.add(
                                            ReservaAdmin(
                                                idReserva = idReserva,
                                                ruta = nombreRuta,
                                                fecha = fecha,
                                                horario = horarioReserva ?: horario,
                                                pasajeros = listaPasajeros,
                                                asientosComprados = totalAsientos,
                                                totalPagado = pagado,
                                                esPasado = esViajePasado,
                                                viajeFueCancelado = estaCancelado,
                                                metodoPago = metodoPagoVal,
                                                pagoConfirmado = pagoConfirmadoVal,
                                                puntoSubida = puntoSubidaVal
                                            )
                                        )
                                    } catch (e: Exception) {}
                                }
                            }
                        }
                    }
                }
                listaReservas.sortedByDescending { it.fecha }
            } catch (e: Exception) {
                emptyList()
            }
        }

        override suspend fun reprogramarReserva(ruta: String, fechaAntigua: String, horarioAntiguo: String, idReserva: String, nuevaFecha: String, nuevoHorario: String): Boolean {
            return try {
                val db = network.FirebaseClient.database
                val refAntigua = db.reference("viajes/$ruta/$fechaAntigua/$horarioAntiguo/reservas/$idReserva")
                val refNueva = db.reference("viajes/$ruta/$nuevaFecha/$nuevoHorario/reservas/$idReserva")

                val snapshot = refAntigua.valueEvents.first()
                if (!snapshot.exists) return false

                val horario = try { snapshot.child("horario").value<String>() } catch (e: Exception) { horarioAntiguo }
                val fechaViaje = try { snapshot.child("fechaViaje").value<String>() } catch (e: Exception) { fechaAntigua }
                val origenDestino = try { snapshot.child("origenDestino").value<String>() } catch (e: Exception) { ruta }
                val totalPagado = try { snapshot.child("totalPagado").value<Int>() } catch (e: Exception) { 0 }

                val updates = mutableMapOf<String, Any?>()

                updates["viajes/$ruta/$nuevaFecha/$nuevoHorario/reservas/$idReserva/horario"] = nuevoHorario
                updates["viajes/$ruta/$nuevaFecha/$nuevoHorario/reservas/$idReserva/fechaViaje"] = nuevaFecha
                updates["viajes/$ruta/$nuevaFecha/$nuevoHorario/reservas/$idReserva/origenDestino"] = origenDestino
                updates["viajes/$ruta/$nuevaFecha/$nuevoHorario/reservas/$idReserva/totalPagado"] = totalPagado

                snapshot.child("pasajeros").children.forEach { pSnap ->
                    val idx = pSnap.key ?: return@forEach
                    pSnap.children.forEach { campo ->
                        val key = campo.key ?: return@forEach
                        val valor = try { campo.value<String>() } catch (e: Exception) {
                            try { campo.value<Int>().toString() } catch (e2: Exception) {
                                try { campo.value<Boolean>().toString() } catch (e3: Exception) { "" }
                            }
                        }
                        updates["viajes/$ruta/$nuevaFecha/$nuevoHorario/reservas/$idReserva/pasajeros/$idx/$key"] = valor
                    }
                }

                val asientos = mutableListOf<Int>()
                snapshot.child("asientos").children.forEach { aSnap ->
                    val asiento = try { aSnap.value<Int>() } catch (e: Exception) { return@forEach }
                    asientos.add(asiento)
                    val idx = aSnap.key ?: return@forEach
                    updates["viajes/$ruta/$nuevaFecha/$nuevoHorario/reservas/$idReserva/asientos/$idx"] = asiento
                    updates["viajes/$ruta/$nuevaFecha/$nuevoHorario/ocupados/$asiento"] = true
                }

                updates["viajes/$ruta/$fechaAntigua/$horarioAntiguo/reservas/$idReserva"] = null
                asientos.forEach { asiento ->
                    updates["viajes/$ruta/$fechaAntigua/$horarioAntiguo/ocupados/$asiento"] = null
                }

                db.reference().updateChildren(updates)
                true
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "BookingRepositoryImpl.reprogramarReserva[$idReserva]")
                false
            }
        }

        override suspend fun obtenerListaAsientosOcupados(ruta: String, fecha: String, horario: String): List<Int> {
            return try {
                val db = network.FirebaseClient.database
                val listaOcupados = mutableListOf<Int>()

                // Leer de ocupados (path con horario)
                val ocupadosSnap = db.reference("viajes/$ruta/$fecha/$horario/ocupados").valueEvents.first()
                if (ocupadosSnap.exists) {
                    ocupadosSnap.children.forEach { child ->
                        val asiento = child.key?.toIntOrNull()
                        if (asiento != null) {
                            val valor = try { child.value<Boolean>() } catch (e: Exception) { false }
                            if (valor) listaOcupados.add(asiento)
                        }
                    }
                }

                // También leer bloqueos temporales vigentes
                val bloqueosSnap = db.reference("viajes/$ruta/$fecha/$horario/bloqueos").valueEvents.first()
                if (bloqueosSnap.exists) {
                    val tiempoActual = Clock.System.now().toEpochMilliseconds()
                    bloqueosSnap.children.forEach { child ->
                        val asiento = child.key?.toIntOrNull()
                        if (asiento != null) {
                            try {
                                val datos = child.value<Map<String, String>>()
                                val expira = datos?.get("expira")?.toLongOrNull() ?: 0L
                                if (expira > tiempoActual) {
                                    listaOcupados.add(asiento)
                                }
                            } catch (_: Exception) {}
                        }
                    }
                }

                listaOcupados.distinct()
            } catch (e: Exception) {
                emptyList()
            }
        }

        /** Reenvía el boleto con un QR específico (el que se guardó en verificaciones del viaje
         *  destino), para que el pasajero reprogramado reciba un boleto escaneable y se entere
         *  del cambio. Best-effort: si falla el correo, no rompe la reprogramación. */
        private suspend fun reenviarBoletoConQR(
            ruta: String, fecha: String, horario: String,
            pasajeros: List<PasajeroInfo>, codigosQR: List<String>,
            email: String, totalPagado: Int, puntoSubida: String
        ) {
            try {
                if (!email.contains("@") || pasajeros.isEmpty()) return
                val pasajerosJson = PasajeroInfo.listaAJsonString(pasajeros)
                val codigosJson = kotlinx.serialization.json.buildJsonArray {
                    codigosQR.forEach { add(kotlinx.serialization.json.JsonPrimitive(it)) }
                }
                val body = kotlinx.serialization.json.buildJsonObject {
                    put("data", kotlinx.serialization.json.buildJsonObject {
                        put("appVersion", kotlinx.serialization.json.JsonPrimitive(network.CloudFunctions.appVersionCode))
                        put("email", kotlinx.serialization.json.JsonPrimitive(email))
                        put("pasajeros", kotlinx.serialization.json.JsonPrimitive(pasajerosJson))
                        put("ruta", kotlinx.serialization.json.JsonPrimitive(ruta))
                        put("fecha", kotlinx.serialization.json.JsonPrimitive(fecha))
                        put("horario", kotlinx.serialization.json.JsonPrimitive(horario))
                        put("totalPagado", kotlinx.serialization.json.JsonPrimitive(totalPagado))
                        if (puntoSubida.isNotBlank()) put("puntoSubida", kotlinx.serialization.json.JsonPrimitive(puntoSubida))
                        put("reprogramado", kotlinx.serialization.json.JsonPrimitive(true))
                        put("codigosQR", codigosJson)
                    })
                }
                val idToken = network.FirebaseClient.auth.currentUser?.getIdToken(true) ?: ""
                network.sharedHttpClient.post(network.CloudFunctions.ENVIAR_BOLETOS) {
                    contentType(ContentType.Application.Json)
                    header("Authorization", "Bearer $idToken")
                    setBody(body.toString())
                }
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "BookingRepositoryImpl.reenviarBoletoConQR")
            }
        }

        /**
         * Punto de subida para el boleto del viaje destino.
         *
         * viajes_programados/.../puntoSubida guarda TODOS los puntos del viaje separados por "|"
         * ("Zibata|La pradera"). Devolverlo crudo pintaba la lista entera como lugar de abordaje.
         * Se respeta el que ya traía el pasajero si el viaje destino lo ofrece; si no, se deja el
         * primero de la lista (y "" si el viaje no tiene puntos).
         */
        private suspend fun puntoSubidaDeViaje(
            ruta: String, fecha: String, horario: String, puntoActual: String = ""
        ): String {
            return try {
                val snap = network.FirebaseClient.database
                    .reference("viajes_programados/$ruta/$fecha/$horario/puntoSubida").valueEvents.first()
                val crudo = if (snap.exists) snap.value<String>() else ""
                val puntos = crudo.split("|").map { it.trim() }.filter { it.isNotBlank() }
                when {
                    puntoActual.isNotBlank() && puntos.any { it.equals(puntoActual, ignoreCase = true) } -> puntoActual
                    else -> puntos.firstOrNull() ?: ""
                }
            } catch (e: Exception) { "" }
        }

        override suspend fun obtenerViajesProgramadosDisponibles(): List<ViajeDisponibleReasignacion> {
            return try {
                val db = network.FirebaseClient.database
                val hoy = org.example.abigail.obtenerFechaHoy() // "YYYY-MM-DD" — comparable como string
                val snap = db.reference("viajes_programados").valueEvents.first()
                val lista = mutableListOf<ViajeDisponibleReasignacion>()
                if (snap.exists) {
                    snap.children.forEach { rutaSnap ->
                        val ruta = rutaSnap.key ?: return@forEach
                        rutaSnap.children.forEach { fechaSnap ->
                            val fecha = fechaSnap.key ?: return@forEach
                            if (fecha < hoy) return@forEach // solo viajes de hoy en adelante
                            fechaSnap.children.forEach { horarioSnap ->
                                val horario = horarioSnap.key ?: return@forEach
                                val capacidad = try { horarioSnap.child("capacidad").value<Int>() } catch (e: Exception) { 13 }
                                val ocupados = try {
                                    val ocSnap = db.reference("viajes/$ruta/$fecha/$horario/ocupados").valueEvents.first()
                                    if (ocSnap.exists) ocSnap.children.count() else 0
                                } catch (e: Exception) { 0 }
                                lista.add(ViajeDisponibleReasignacion(ruta, fecha, horario, capacidad, ocupados))
                            }
                        }
                    }
                }
                lista.sortedWith(compareBy({ it.fecha }, { it.ruta }, { it.horario }))
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "BookingRepositoryImpl.obtenerViajesProgramadosDisponibles")
                emptyList()
            }
        }

        /**
         * Números de asiento REALES del vehículo asignado al viaje.
         *
         * La numeración del layout es manual (los asientos ya vienen numerados de fábrica), así
         * que no se puede asumir 1..capacidad: un vehículo puede saltarse números o empezar en
         * otro. Si el viaje no tiene layout, se cae al rango clásico por compatibilidad.
         */
        private suspend fun numerosDeAsiento(ruta: String, fecha: String, horario: String, capacidad: Int): List<Int> {
            val delLayout = try {
                obtenerLayoutDeViaje(ruta, fecha, horario)
                    ?.filas?.flatMap { f -> f.asientos.filter { it > 0 } }?.distinct()?.sorted()
            } catch (e: Exception) { null }
            return if (!delLayout.isNullOrEmpty()) delLayout else (1..capacidad).toList()
        }

        override suspend fun obtenerAsientosLibresViaje(ruta: String, fecha: String, horario: String): Pair<Int, List<Int>> {
            return try {
                val capacidad = try {
                    val capSnap = network.FirebaseClient.database.reference("viajes_programados/$ruta/$fecha/$horario/capacidad").valueEvents.first()
                    if (capSnap.exists) capSnap.value<Int>() else 13
                } catch (e: Exception) { 13 }
                val ocupados = obtenerListaAsientosOcupados(ruta, fecha, horario).toSet()
                val todos = numerosDeAsiento(ruta, fecha, horario, capacidad)
                Pair(capacidad, todos.filter { it !in ocupados })
            } catch (e: Exception) { Pair(13, emptyList()) }
        }

        override suspend fun reasignarReservaCancelada(cancelacionId: String, rutaNueva: String, fechaNueva: String, horarioNuevo: String, asientosElegidos: List<Int>?): ReasignacionResultado {
            return try {
                val db = network.FirebaseClient.database
                val cancelSnap = db.reference("historial_cancelaciones/$cancelacionId").valueEvents.first()
                if (!cancelSnap.exists) return ReasignacionResultado(false, "No se encontró el registro de cancelación.")

                // Reconstruir pasajeros desde el snapshot guardado al cancelar
                val pasajerosBase = mutableListOf<PasajeroInfo>()
                cancelSnap.child("snapPasajeros").children.forEach { p ->
                    try { pasajerosBase.add(PasajeroInfo.fromSnapshot(p)) } catch (e: Exception) {}
                }
                if (pasajerosBase.isEmpty()) {
                    return ReasignacionResultado(false, "Esta cancelación no tiene datos del pasajero (es anterior a esta función). No se puede reasignar automáticamente; haz el reembolso o crea la reserva manualmente.")
                }

                val correoTitular = try { cancelSnap.child("correo").value<String>() } catch (e: Exception) { "" }
                    .ifBlank { pasajerosBase.firstOrNull { it.correo.isNotBlank() }?.correo ?: "" }
                val totalPagado = try { cancelSnap.child("snapTotalPagado").value<Int>() } catch (e: Exception) { 0 }
                val metodoPago = try { cancelSnap.child("snapMetodoPago").value<String>() } catch (e: Exception) { "" }
                val puntoSubidaPrevio = try { cancelSnap.child("snapPuntoSubida").value<String>() } catch (e: Exception) { "" }

                val cantidad = pasajerosBase.size

                // Capacidad del viaje destino + asientos libres (numeración real del vehículo)
                val capacidad = try {
                    val capSnap = db.reference("viajes_programados/$rutaNueva/$fechaNueva/$horarioNuevo/capacidad").valueEvents.first()
                    if (capSnap.exists) capSnap.value<Int>() else 13
                } catch (e: Exception) { 13 }
                val ocupados = obtenerListaAsientosOcupados(rutaNueva, fechaNueva, horarioNuevo).toSet()
                val libres = numerosDeAsiento(rutaNueva, fechaNueva, horarioNuevo, capacidad).filter { it !in ocupados }
                if (libres.size < cantidad) {
                    return ReasignacionResultado(false, "El viaje destino solo tiene ${libres.size} asiento(s) libre(s); se necesitan $cantidad.")
                }

                // Asignación de asientos:
                //  - Si el admin eligió asientos → usarlos (validados).
                //  - Si no → mantener el asiento ORIGINAL de cada pasajero si está libre; rellenar el resto.
                val asientosNuevos: List<Int> = if (asientosElegidos != null && asientosElegidos.isNotEmpty()) {
                    val invalidos = asientosElegidos.filter { it !in libres }
                    if (asientosElegidos.size != cantidad || invalidos.isNotEmpty()) {
                        return ReasignacionResultado(false, "Asientos elegidos no válidos o ya ocupados: ${invalidos.joinToString(", ").ifBlank { "cantidad incorrecta" }}.")
                    }
                    asientosElegidos
                } else {
                    val libresSet = libres.toSet()
                    val tomados = mutableSetOf<Int>()
                    val resultado = IntArray(cantidad) { -1 }
                    // 1ª pasada: mantener original si está libre
                    pasajerosBase.forEachIndexed { i, p ->
                        val orig = p.asiento
                        if (orig in libresSet && orig !in tomados) { resultado[i] = orig; tomados.add(orig) }
                    }
                    // 2ª pasada: rellenar los que no se pudieron mantener, con libres más bajos
                    val restantes = libres.filter { it !in tomados }.iterator()
                    for (i in resultado.indices) if (resultado[i] == -1) resultado[i] = restantes.next()
                    resultado.toList()
                }

                val pathNuevo = "viajes/$rutaNueva/$fechaNueva/$horarioNuevo"
                val nuevoReservaId = db.reference("$pathNuevo/reservas").push().key ?: "resv_${Clock.System.now().toEpochMilliseconds()}"
                val ts = Clock.System.now().toEpochMilliseconds().toString()

                // Reclamar cada asiento con set + read-back inmediato. Reduce mucho la
                // ventana de doble venta en reasignaciones concurrentes con Stripe/webhook.
                // No es 100% atómico como transaction (gitlive-firebase limita esa API),
                // pero cierra la carrera práctica >99%. Rollback si un competidor ganó.
                val asientosGanados = mutableListOf<Int>()
                var perdimosCompetencia = false
                for (asiento in asientosNuevos) {
                    val ref = db.reference("$pathNuevo/ocupados/$asiento")
                    // Read pre-check para no pisar ocupado existente por otra reserva.
                    val yaOcupado = try {
                        val s = ref.valueEvents.first()
                        s.exists && (try { s.value<Boolean>() } catch (_: Exception) { false })
                    } catch (_: Exception) { false }
                    if (yaOcupado) { perdimosCompetencia = true; break }
                    ref.setValue(true)
                    kotlinx.coroutines.delay(80)
                    // Read-back: si nuestro true sigue ahí y no fue reescrito, es nuestro.
                    val confirmado = try {
                        val s = ref.valueEvents.first()
                        s.exists && (try { s.value<Boolean>() } catch (_: Exception) { false })
                    } catch (_: Exception) { false }
                    if (!confirmado) { perdimosCompetencia = true; break }
                    asientosGanados.add(asiento)
                }
                if (perdimosCompetencia) {
                    val rollback = mutableMapOf<String, Any?>()
                    for (a in asientosGanados) rollback["$pathNuevo/ocupados/$a"] = null
                    if (rollback.isNotEmpty()) db.reference().updateChildren(rollback)
                    return ReasignacionResultado(false, "Uno o más asientos fueron ocupados por otra reserva en paralelo. Vuelve a elegir.")
                }

                val updates = mutableMapOf<String, Any?>()
                // Respeta el punto que traía la reserva cancelada si el viaje destino lo ofrece.
                // Sin escribirlo, la reserva reasignada salía "Sin asignar" en Gestión de Boletos.
                val puntoSubidaNuevo = puntoSubidaDeViaje(rutaNueva, fechaNueva, horarioNuevo, puntoSubidaPrevio)
                updates["$pathNuevo/reservas/$nuevoReservaId/totalPagado"] = totalPagado
                updates["$pathNuevo/reservas/$nuevoReservaId/fechaViaje"] = fechaNueva
                updates["$pathNuevo/reservas/$nuevoReservaId/horario"] = horarioNuevo
                updates["$pathNuevo/reservas/$nuevoReservaId/origenDestino"] = rutaNueva
                updates["$pathNuevo/reservas/$nuevoReservaId/timestamp"] = ts
                updates["$pathNuevo/reservas/$nuevoReservaId/metodoPago"] = metodoPago
                updates["$pathNuevo/reservas/$nuevoReservaId/puntoSubida"] = puntoSubidaNuevo
                updates["$pathNuevo/reservas/$nuevoReservaId/reasignadoDe"] = cancelacionId

                val pasajerosReasignados = mutableListOf<PasajeroInfo>()
                val codigosQR = mutableListOf<String>()
                pasajerosBase.forEachIndexed { index, p ->
                    val asiento = asientosNuevos[index]
                    val pNuevo = p.copy(asiento = asiento)
                    pasajerosReasignados.add(pNuevo)
                    updates["$pathNuevo/reservas/$nuevoReservaId/pasajeros/$index/nombre"] = pNuevo.nombre
                    updates["$pathNuevo/reservas/$nuevoReservaId/pasajeros/$index/asiento"] = asiento
                    updates["$pathNuevo/reservas/$nuevoReservaId/pasajeros/$index/telefono"] = pNuevo.telefono
                    updates["$pathNuevo/reservas/$nuevoReservaId/pasajeros/$index/correo"] = pNuevo.correo
                    updates["$pathNuevo/reservas/$nuevoReservaId/pasajeros/$index/parada"] = pNuevo.parada
                    updates["$pathNuevo/reservas/$nuevoReservaId/pasajeros/$index/maletasExtras"] = pNuevo.maletasExtras
                    updates["$pathNuevo/reservas/$nuevoReservaId/pasajeros/$index/llevaMascota"] = pNuevo.llevaMascota
                    updates["$pathNuevo/reservas/$nuevoReservaId/asientos/$index"] = asiento
                    // ocupados/$asiento ya fue reclamado por runTransaction arriba.
                    val codigo = "SMARTVAN-$ts-$asiento"
                    codigosQR.add(codigo)
                    updates["$pathNuevo/verificaciones/$codigo"] = mapOf(
                        "asiento" to asiento,
                        "pasajero" to pNuevo.nombre,
                        "verificado" to false,
                        "reservaId" to nuevoReservaId
                    )
                }

                // Marcar la cancelación como reasignada (seguimiento)
                val operadorUid = network.FirebaseClient.auth.currentUser?.uid ?: ""
                val operadorNombre = try {
                    val u = db.reference("usuarios/$operadorUid").valueEvents.first()
                    val n = try { u.child("nombre").value<String?>() } catch (e: Exception) { null }
                    val c = try { u.child("correo").value<String?>() } catch (e: Exception) { null }
                    n?.takeIf { it.isNotBlank() } ?: c?.takeIf { it.isNotBlank() }
                        ?: network.FirebaseClient.auth.currentUser?.email ?: ""
                } catch (e: Exception) { network.FirebaseClient.auth.currentUser?.email ?: "" }

                updates["historial_cancelaciones/$cancelacionId/estadoResolucion"] = "reasignado"
                updates["historial_cancelaciones/$cancelacionId/reasignadoA"] = "$rutaNueva | $fechaNueva | $horarioNuevo"
                updates["historial_cancelaciones/$cancelacionId/nuevaReservaId"] = nuevoReservaId
                updates["historial_cancelaciones/$cancelacionId/resueltoPorUid"] = operadorUid
                updates["historial_cancelaciones/$cancelacionId/resueltoPorNombre"] = operadorNombre
                updates["historial_cancelaciones/$cancelacionId/fechaResolucion"] = ts

                // Auditoría
                val audId = db.reference("auditoria").push().key ?: "aud_$ts"
                updates["auditoria/$audId"] = mapOf(
                    "accion" to "reserva_reasignada",
                    "operadorUid" to operadorUid,
                    "operadorNombre" to operadorNombre,
                    "ruta" to rutaNueva,
                    "fecha" to fechaNueva,
                    "horario" to horarioNuevo,
                    "reservaId" to nuevoReservaId,
                    "monto" to totalPagado,
                    "detalle" to "Reasignó pasajero(s) de cancelación a $rutaNueva $fechaNueva $horarioNuevo",
                    "timestamp" to ts
                )

                db.reference().updateChildren(updates)

                // Enviar el boleto nuevo (nuevo horario, nuevo QR, nueva info) al titular
                reenviarBoletoConQR(
                    rutaNueva, fechaNueva, horarioNuevo, pasajerosReasignados, codigosQR,
                    correoTitular, totalPagado,
                    puntoSubidaNuevo
                )

                val avisoCorreo = if (correoTitular.contains("@")) " Correo enviado a $correoTitular." else " (Sin correo del pasajero: no se envió boleto.)"
                ReasignacionResultado(true, "Reasignado a $rutaNueva · $fechaNueva · $horarioNuevo (asiento(s) ${asientosNuevos.joinToString(", ")}).$avisoCorreo")
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "BookingRepositoryImpl.reasignarReservaCancelada[$cancelacionId]")
                ReasignacionResultado(false, "Error al reasignar: ${e.message}")
            }
        }

        override suspend fun reprogramarReservaCompleta(rutaAntigua: String, fechaAntigua: String, horarioAntiguo: String, idReserva: String, rutaNueva: String, fechaNueva: String, horarioNuevo: String, nuevosAsientos: List<Int>): Boolean {
            return try {
                val db = network.FirebaseClient.database
                val pathAntiguo = "viajes/$rutaAntigua/$fechaAntigua/$horarioAntiguo"
                val pathNuevo = "viajes/$rutaNueva/$fechaNueva/$horarioNuevo"

                val fullPath = "$pathAntiguo/reservas/$idReserva"
                val refAntigua = db.reference(fullPath)
                val snapshot = refAntigua.valueEvents.first()
                if (!snapshot.exists) {
                    return false
                }

                // Asientos actuales de la reserva (para no detectar conflicto consigo misma)
                val asientosActuales = snapshot.child("asientos").children.mapNotNull {
                    try { it.value<Int>() } catch (e: Exception) { null }
                }
                // Guardia: abortar si un asiento destino ya está ocupado por OTRA reserva
                for (asiento in nuevosAsientos) {
                    if (CalculoReprogramacion.requiereChequeoOcupacion(pathNuevo == pathAntiguo, asiento, asientosActuales)) {
                        val ocSnap = db.reference("$pathNuevo/ocupados/$asiento").valueEvents.first()
                        val ocupado = ocSnap.exists && (try { ocSnap.value<Boolean>() } catch (e: Exception) { false })
                        if (ocupado) return false
                    }
                }

                val updates = mutableMapOf<String, Any?>()
                val ts = Clock.System.now().toEpochMilliseconds().toString()

                updates["$pathNuevo/reservas/$idReserva/horario"] = horarioNuevo
                updates["$pathNuevo/reservas/$idReserva/fechaViaje"] = fechaNueva
                updates["$pathNuevo/reservas/$idReserva/origenDestino"] = rutaNueva

                val totalPagado = try { snapshot.child("totalPagado").value<Int>() } catch (e: Exception) { 0 }
                updates["$pathNuevo/reservas/$idReserva/totalPagado"] = totalPagado

                // La reserva vieja se borra abajo, así que hay que ARRASTRAR todo lo demás:
                // puntoSubida, metodoPago, paymentIntentId/pagoManualId, timestamp... Antes se
                // copiaba solo una lista blanca y al reprogramar el boleto quedaba "Sin asignar"
                // y sin método de pago. Se copia todo y se sobrescribe solo lo que cambia.
                val yaManejados = setOf("horario", "fechaViaje", "origenDestino", "totalPagado", "pasajeros", "asientos")
                snapshot.children.forEach { campo ->
                    val key = campo.key ?: return@forEach
                    if (key in yaManejados) return@forEach
                    val valor: Any? = try { campo.value<String>() } catch (e: Exception) {
                        try { campo.value<Int>() } catch (e2: Exception) {
                            try { campo.value<Boolean>() } catch (e3: Exception) { null }
                        }
                    }
                    if (valor != null) updates["$pathNuevo/reservas/$idReserva/$key"] = valor
                }

                // Copia de campos al destino + lista de pasajeros (con asiento nuevo) para el correo
                val pasajerosReprog = mutableListOf<PasajeroInfo>()
                snapshot.child("pasajeros").children.forEachIndexed { index, pSnap ->
                    val idx = pSnap.key ?: return@forEachIndexed
                    val nuevoAsiento = nuevosAsientos.getOrElse(index) { 0 }
                    val base = try { PasajeroInfo.fromSnapshot(pSnap) } catch (e: Exception) { PasajeroInfo() }
                    pasajerosReprog.add(base.copy(asiento = nuevoAsiento))
                    pSnap.children.forEach { campo ->
                        val key = campo.key ?: return@forEach
                        if (key == "asiento") {
                            updates["$pathNuevo/reservas/$idReserva/pasajeros/$idx/asiento"] = nuevoAsiento
                        } else {
                            val valor = try { campo.value<String>() } catch (e: Exception) {
                                try { campo.value<Int>() } catch (e2: Exception) {
                                    try { campo.value<Boolean>() } catch (e3: Exception) { "" }
                                }
                            }
                            updates["$pathNuevo/reservas/$idReserva/pasajeros/$idx/$key"] = valor
                        }
                    }
                }

                nuevosAsientos.forEachIndexed { index, asiento ->
                    updates["$pathNuevo/reservas/$idReserva/asientos/$index"] = asiento
                    updates["$pathNuevo/ocupados/$asiento"] = true
                }

                // Crear verificaciones (QR) nuevas en el viaje destino — escaneables al abordar
                val codigosQR = mutableListOf<String>()
                pasajerosReprog.forEach { p ->
                    val codigo = "SMARTVAN-$ts-${p.asiento}"
                    codigosQR.add(codigo)
                    updates["$pathNuevo/verificaciones/$codigo"] = mapOf(
                        "asiento" to p.asiento,
                        "pasajero" to p.nombre,
                        "verificado" to false,
                        "reservaId" to idReserva
                    )
                }

                // Liberar ocupados viejos
                snapshot.child("asientos").children.forEach { aSnap ->
                    val asiento = try { aSnap.value<Int>() } catch (e: Exception) { return@forEach }
                    updates["$pathAntiguo/ocupados/$asiento"] = null
                }
                // Invalidar verificaciones (QR) viejas de esta reserva en el viaje origen
                val verifViejas = db.reference("$pathAntiguo/verificaciones").valueEvents.first()
                if (verifViejas.exists) {
                    verifViejas.children.forEach { v ->
                        val rid = try { v.child("reservaId").value<String>() } catch (e: Exception) { "" }
                        if (rid == idReserva) updates["$pathAntiguo/verificaciones/${v.key}"] = null
                    }
                }

                updates["$pathAntiguo/reservas/$idReserva"] = null

                db.reference().updateChildren(updates)

                // Reenviar boleto con el QR nuevo al titular
                val emailTitular = pasajerosReprog.firstOrNull()?.correo ?: ""
                reenviarBoletoConQR(rutaNueva, fechaNueva, horarioNuevo, pasajerosReprog, codigosQR,
                    emailTitular, totalPagado,
                    puntoSubidaDeViaje(rutaNueva, fechaNueva, horarioNuevo,
                        try { snapshot.child("puntoSubida").value<String>() } catch (e: Exception) { "" }))
                true
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "BookingRepositoryImpl.reprogramarReservaCompleta[$idReserva]")
                false
            }
        }

        override suspend fun dividirYReprogramarGrupo(rutaAntigua: String, fechaAntigua: String, horarioAntiguo: String, idReservaOriginal: String, pasajerosAMover: List<PasajeroInfo>, rutaNueva: String, fechaNueva: String, horarioNuevo: String, nuevosAsientos: List<Int>): Boolean {
            return try {
                val db = network.FirebaseClient.database
                val pathAntiguo = "viajes/$rutaAntigua/$fechaAntigua/$horarioAntiguo"
                val pathNuevo = "viajes/$rutaNueva/$fechaNueva/$horarioNuevo"

                val fullPath = "$pathAntiguo/reservas/$idReservaOriginal"
                val refAntigua = db.reference(fullPath)
                val snapReserva = refAntigua.valueEvents.first()
                if (!snapReserva.exists) return false

                val pasajerosOriginales = mutableListOf<PasajeroInfo>()
                snapReserva.child("pasajeros").children.forEach { pSnap ->
                    try { pasajerosOriginales.add(PasajeroInfo.fromSnapshot(pSnap)) } catch (e: Exception) {}
                }

                val asientosOriginales = mutableListOf<Int>()
                snapReserva.child("asientos").children.forEach { aSnap ->
                    try { asientosOriginales.add(aSnap.value<Int>()) } catch (e: Exception) {}
                }

                val totalPagadoOriginal = try { snapReserva.child("totalPagado").value<Int>() } catch (e: Exception) { 0 }
                val origenDestino = try { snapReserva.child("origenDestino").value<String>() } catch (e: Exception) { rutaAntigua }

                val nombresAMover = pasajerosAMover.map { it.nombre }
                val pasajerosQueSeQuedan = pasajerosOriginales.filterNot { nombresAMover.contains(it.nombre) }
                val asientosAMover = pasajerosAMover.map { it.asiento }
                val asientosQueSeQuedan = asientosOriginales.filterNot { asientosAMover.contains(it) }

                val division = CalculoReprogramacion.dividirPago(totalPagadoOriginal, pasajerosOriginales.size, pasajerosAMover.size)
                val pagadoPorLosQueSeVan = division.pagaQuienSeVa
                val pagadoPorLosQueSeQuedan = division.pagaQuienSeQueda

                // Guardia: abortar si un asiento destino ya está ocupado por OTRA reserva
                for (asiento in nuevosAsientos) {
                    if (CalculoReprogramacion.requiereChequeoOcupacion(pathNuevo == pathAntiguo, asiento, asientosOriginales)) {
                        val ocSnap = db.reference("$pathNuevo/ocupados/$asiento").valueEvents.first()
                        val ocupado = ocSnap.exists && (try { ocSnap.value<Boolean>() } catch (e: Exception) { false })
                        if (ocupado) return false
                    }
                }

                // Usar un solo updateChildren atómico (borrar + escribir)
                val updates = mutableMapOf<String, Any?>()
                updates["$pathAntiguo/reservas/$idReservaOriginal/pasajeros"] = null
                updates["$pathAntiguo/reservas/$idReservaOriginal/asientos"] = null

                updates["$pathAntiguo/reservas/$idReservaOriginal/totalPagado"] = pagadoPorLosQueSeQuedan

                pasajerosQueSeQuedan.forEachIndexed { index, p ->
                    updates["$pathAntiguo/reservas/$idReservaOriginal/pasajeros/$index/nombre"] = p.nombre
                    updates["$pathAntiguo/reservas/$idReservaOriginal/pasajeros/$index/asiento"] = p.asiento
                    updates["$pathAntiguo/reservas/$idReservaOriginal/pasajeros/$index/telefono"] = p.telefono
                    updates["$pathAntiguo/reservas/$idReservaOriginal/pasajeros/$index/correo"] = p.correo
                    updates["$pathAntiguo/reservas/$idReservaOriginal/pasajeros/$index/parada"] = p.parada
                    updates["$pathAntiguo/reservas/$idReservaOriginal/pasajeros/$index/maletasExtras"] = p.maletasExtras
                    updates["$pathAntiguo/reservas/$idReservaOriginal/pasajeros/$index/llevaMascota"] = p.llevaMascota
                }

                asientosQueSeQuedan.forEachIndexed { index, asiento ->
                    updates["$pathAntiguo/reservas/$idReservaOriginal/asientos/$index"] = asiento
                }

                asientosAMover.forEach { asiento ->
                    updates["$pathAntiguo/ocupados/$asiento"] = null
                }

                val ts = Clock.System.now().toEpochMilliseconds().toString()
                val nuevoId = db.reference("viajes").push().key ?: "SPLIT_${kotlin.random.Random.nextInt(100000, 999999)}"
                updates["$pathNuevo/reservas/$nuevoId/horario"] = horarioNuevo
                updates["$pathNuevo/reservas/$nuevoId/fechaViaje"] = fechaNueva
                updates["$pathNuevo/reservas/$nuevoId/origenDestino"] = origenDestino
                updates["$pathNuevo/reservas/$nuevoId/totalPagado"] = pagadoPorLosQueSeVan
                // La reserva del grupo movido nacía sin estos: quedaba "Sin asignar" y sin método
                // de pago, aunque el grupo original sí los tenía.
                val puntoSubidaMovidos = puntoSubidaDeViaje(
                    rutaNueva, fechaNueva, horarioNuevo,
                    try { snapReserva.child("puntoSubida").value<String>() } catch (e: Exception) { "" }
                )
                updates["$pathNuevo/reservas/$nuevoId/puntoSubida"] = puntoSubidaMovidos
                updates["$pathNuevo/reservas/$nuevoId/metodoPago"] =
                    try { snapReserva.child("metodoPago").value<String>() } catch (e: Exception) { "" }
                updates["$pathNuevo/reservas/$nuevoId/timestamp"] = ts

                val codigosQR = mutableListOf<String>()
                val pasajerosMovidos = mutableListOf<PasajeroInfo>()
                pasajerosAMover.forEachIndexed { index, p ->
                    val nuevoAsiento = nuevosAsientos.getOrElse(index) { 0 }
                    pasajerosMovidos.add(p.copy(asiento = nuevoAsiento))
                    updates["$pathNuevo/reservas/$nuevoId/pasajeros/$index/nombre"] = p.nombre
                    updates["$pathNuevo/reservas/$nuevoId/pasajeros/$index/asiento"] = nuevoAsiento
                    updates["$pathNuevo/reservas/$nuevoId/pasajeros/$index/telefono"] = p.telefono
                    updates["$pathNuevo/reservas/$nuevoId/pasajeros/$index/correo"] = p.correo
                    updates["$pathNuevo/reservas/$nuevoId/pasajeros/$index/parada"] = p.parada
                    updates["$pathNuevo/reservas/$nuevoId/pasajeros/$index/maletasExtras"] = p.maletasExtras
                    updates["$pathNuevo/reservas/$nuevoId/pasajeros/$index/llevaMascota"] = p.llevaMascota
                    updates["$pathNuevo/reservas/$nuevoId/asientos/$index"] = nuevoAsiento
                    updates["$pathNuevo/ocupados/$nuevoAsiento"] = true
                    // QR nuevo escaneable para el pasajero movido
                    val codigo = "SMARTVAN-$ts-$nuevoAsiento"
                    codigosQR.add(codigo)
                    updates["$pathNuevo/verificaciones/$codigo"] = mapOf(
                        "asiento" to nuevoAsiento,
                        "pasajero" to p.nombre,
                        "verificado" to false,
                        "reservaId" to nuevoId
                    )
                }

                // Invalidar las verificaciones (QR) viejas SOLO de los asientos que se movieron
                val verifViejas = db.reference("$pathAntiguo/verificaciones").valueEvents.first()
                if (verifViejas.exists) {
                    verifViejas.children.forEach { v ->
                        val rid = try { v.child("reservaId").value<String>() } catch (e: Exception) { "" }
                        val asv = try { v.child("asiento").value<Int>() } catch (e: Exception) { -1 }
                        if (rid == idReservaOriginal && asv in asientosAMover) {
                            updates["$pathAntiguo/verificaciones/${v.key}"] = null
                        }
                    }
                }

                db.reference().updateChildren(updates)

                // Reenviar boleto del grupo movido con el QR nuevo
                val emailMovidos = pasajerosAMover.firstOrNull()?.correo?.ifBlank { pasajerosOriginales.firstOrNull()?.correo ?: "" }
                    ?: (pasajerosOriginales.firstOrNull()?.correo ?: "")
                reenviarBoletoConQR(rutaNueva, fechaNueva, horarioNuevo, pasajerosMovidos, codigosQR,
                    emailMovidos, pagadoPorLosQueSeVan, puntoSubidaMovidos)
                true
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "BookingRepositoryImpl.dividirYReprogramarGrupo[$idReservaOriginal]")
                false
            }
        }
        private fun parsearTimestamp(valor: Any?): Long {
            return when (valor) {
                is String -> valor.toLongOrNull() ?: 0L
                is Long -> valor
                is Double -> valor.toLong()
                is Int -> valor.toLong()
                is Map<*, *> -> {
                    val high = (valor["high_1"] as? Number)?.toLong() ?: 0L
                    val low = (valor["low_1"] as? Number)?.toLong() ?: 0L
                    (high shl 32) or (low and 0xFFFFFFFFL)
                }
                else -> 0L
            }
        }
        // FIX #1: Optimistic write-then-verify para eliminar race condition.
        // Antes: read → check → write (dos usuarios podían leer "libre" simultáneamente).
        // Ahora: write primero → read back → verificar si somos el ganador (timestamp más bajo).
        // Si perdemos, revertimos nuestra escritura.
        override suspend fun bloquearAsientoTemporal(ruta: String,fecha: String,horario: String, asiento: Int, miIdSesion: String): Long? {
            return try {
                val db = network.FirebaseClient.database
                val basePath = "viajes/$ruta/$fecha/$horario/bloqueos/$asiento"
                val tiempoActual = Clock.System.now().toEpochMilliseconds()
                val limiteExpiracion = tiempoActual - (10 * 60 * 1000)

                // Paso 1: Verificar pago_pendiente antes de escribir
                val pagoPendienteSnap = db.reference("$basePath/pago_pendiente").valueEvents.first()
                if (pagoPendienteSnap.exists) {
                    return null
                }

                // Paso 2: Escribir OPTIMISTAMENTE nuestro bloqueo
                val miRef = db.reference("$basePath/$miIdSesion")
                miRef.setValue(tiempoActual.toString())
                miRef.onDisconnect().removeValue()

                // Paso 3: Pequeña pausa para que escrituras concurrentes se propaguen
                kotlinx.coroutines.delay(150)

                // Paso 4: Read-back — leer TODO el nodo para verificar competidores
                val verificacion = db.reference(basePath).valueEvents.first()
                if (!verificacion.exists) {
                    // Nuestro write desapareció (raro), fallar
                    return null
                }

                // Verificar si hay otro bloqueo ajeno vigente con timestamp menor (ganador)
                var hayGanadorAjeno = false
                verificacion.children.forEach { child ->
                    val sesionId = child.key ?: return@forEach
                    if (sesionId == "pago_pendiente") {
                        // Si apareció un pago_pendiente entre write y verify, revertir
                        hayGanadorAjeno = true
                        return@forEach
                    }
                    if (sesionId == miIdSesion) return@forEach // skip nuestro propio bloqueo
                    val tiempo = try {
                        parsearTimestamp(child.value<Any?>())
                    } catch (e: Exception) { 0L }
                    if (tiempo > limiteExpiracion) {
                        // Hay un bloqueo ajeno vigente — comparar timestamps
                        if (tiempo <= tiempoActual) {
                            hayGanadorAjeno = true // el otro llegó primero o al mismo tiempo
                        }
                    }
                }

                if (hayGanadorAjeno) {
                    // Perdimos la carrera, revertir nuestra escritura
                    miRef.removeValue()
                    try { miRef.onDisconnect().cancel() } catch (_: Exception) {}
                    null
                } else {
                    misSesionesActivas["$ruta-$fecha-$horario-$asiento"] = miIdSesion
                    tiempoActual + (10 * 60 * 1000)
                }
            } catch (e: Exception) {
                println("[SmartVan] bloquearAsiento error: ${e.message} | ruta=$ruta fecha=$fecha asiento=$asiento")
                null
            }
        }

        override suspend fun liberarAsientoBloqueado(ruta: String, fecha: String, horario: String, asiento: Int) {
            try {
                val claveLocal = "$ruta-$fecha-$horario-$asiento"
                val miIdSesion = misSesionesActivas[claveLocal]


                if (miIdSesion != null) {
                    val ref = network.FirebaseClient.database
                        .reference("viajes/$ruta/$fecha/$horario/bloqueos/$asiento/$miIdSesion")
                    ref.removeValue()
                    try { ref.onDisconnect().cancel() } catch(e:Exception){}
                    misSesionesActivas.remove(claveLocal)
                } else {
                }
            } catch (e: Exception) {
            }
        }

        override fun observarAsientosNoDisponibles(ruta: String, fecha: String, horario: String, miIdSesion: String): Flow<List<Int>> {
            val db = network.FirebaseClient.database

            val flowOcupados = db.reference("viajes/$ruta/$fecha/$horario/ocupados").valueEvents
            val flowBloqueos = db.reference("viajes/$ruta/$fecha/$horario/bloqueos").valueEvents

            return flowOcupados.combine(flowBloqueos) { snapOcupados, snapBloqueos ->

                val ocupados: List<Int> = try {
                    if (!snapOcupados.exists) emptyList()
                    else {
                        val lista = mutableListOf<Int>()
                        snapOcupados.children.forEach { child ->
                            val asiento = child.key?.toIntOrNull()
                            if (asiento != null) {
                                val valor = try { child.value<Boolean>() } catch (e: Exception) {
                                    try { child.value<String>().toBoolean() } catch (e2: Exception) { false }
                                }
                                if (valor) {
                                    lista.add(asiento)
                                }
                            }
                        }
                        lista
                    }
                } catch (e: Exception) {
                    emptyList()
                }

                // FIX #2: Usar children iteration en vez de deserialización genérica Map<String, Map<String, String>>
                // que fallaba cuando pago_pendiente contenía un sub-mapa anidado (el cast nunca tenía éxito).
                val bloqueados: List<Int> = try {
                    if (!snapBloqueos.exists) emptyList()
                    else {
                        val tiempoActual = Clock.System.now().toEpochMilliseconds()
                        val limiteExpiracion = tiempoActual - (10 * 60 * 1000)
                        // Un bloqueo con timestamp futuro nunca expiraría y dejaría el asiento
                        // invendible para siempre. Se ignora, tolerando desfase de reloj del device.
                        val limiteFuturo = tiempoActual + (2 * 60 * 1000)

                        val resultado = mutableListOf<Int>()
                        snapBloqueos.children.forEach { asientoSnap ->
                            val asientoNum = asientoSnap.key?.toIntOrNull() ?: return@forEach
                            val pagoPendiente = asientoSnap.child("pago_pendiente")
                            if (pagoPendiente.exists) {
                                // Leer expiraEn desde el sub-nodo correcto
                                val expiraEn = try {
                                    pagoPendiente.child("expiraEn").value<String>().toLongOrNull() ?: 0L
                                } catch (e: Exception) {
                                    try { pagoPendiente.child("expiraEn").value<Long>() } catch (_: Exception) { 0L }
                                }
                                if (expiraEn > tiempoActual) {
                                    resultado.add(asientoNum)
                                }
                            } else {
                                // Bloqueos temporales normales por sesión
                                var hayGanadorAjeno = false
                                asientoSnap.children.forEach { sesionSnap ->
                                    val sesionId = sesionSnap.key ?: return@forEach
                                    if (sesionId == miIdSesion) return@forEach
                                    val tiempo = try {
                                        parsearTimestamp(sesionSnap.value<Any?>())
                                    } catch (e: Exception) { 0L }
                                    if (tiempo > limiteExpiracion && tiempo <= limiteFuturo) {
                                        hayGanadorAjeno = true
                                    }
                                }
                                if (hayGanadorAjeno) {
                                    resultado.add(asientoNum)
                                }
                            }
                        }
                        resultado
                    }
                } catch (e: Exception) {
                    emptyList()
                }

                val resultado = (ocupados + bloqueados).distinct()
                resultado
            }
        }

        override suspend fun reasignarLogisticaMasiva(choferSaliente: String, choferEntrante: String, vehiculoNuevo: String, aPartirDeFecha: String, choferEntranteUid: String): Int {
            return try {
                val db = network.FirebaseClient.database
                val snapshot = db.reference("viajes_programados").valueEvents.first()
                if (!snapshot.exists) return 0

                var viajesModificados = 0
                val updates = mutableMapOf<String, Any?>()

                // Iterar children manualmente — viajes_programados contiene
                // campos Int (capacidad, vehiculoId) que rompen Map<String,String>
                snapshot.children.forEach { rutaSnap ->
                    val ruta = rutaSnap.key ?: return@forEach
                    rutaSnap.children.forEach { fechaSnap ->
                        val fecha = fechaSnap.key ?: return@forEach
                        if (fecha >= aPartirDeFecha) {
                            fechaSnap.children.forEach { horarioSnap ->
                                val horario = horarioSnap.key ?: return@forEach
                                val chofer = try { horarioSnap.child("chofer").value<String>() } catch (e: Exception) { "" }
                                if (chofer == choferSaliente) {
                                    updates["viajes_programados/$ruta/$fecha/$horario/chofer"] = choferEntrante
                                    updates["viajes_programados/$ruta/$fecha/$horario/vehiculo"] = vehiculoNuevo
                                    // Revincular por uid al nuevo chofer: las rules exigen que
                                    // choferUid coincida con auth.uid para escanear QR/asistencia/GPS.
                                    updates["viajes_programados/$ruta/$fecha/$horario/choferUid"] =
                                        choferEntranteUid.ifBlank { null }
                                    viajesModificados++
                                }
                            }
                        }
                    }
                }

                if (updates.isNotEmpty()) {
                    db.reference().updateChildren(updates)
                }
                viajesModificados
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "BookingRepositoryImpl.reasignarLogisticaMasiva[$choferSaliente]")
                0
            }
        }

        override suspend fun cancelarReservaAdmin(ruta: String, fecha: String, horario: String, idReserva: String): Boolean {
            return try {

                val pathReserva = "viajes/$ruta/$fecha/$horario/reservas/$idReserva"
                val db = network.FirebaseClient.database

                val snapshot = db.reference(pathReserva).valueEvents.first()
                if (!snapshot.exists) {
                    return false
                }

                val updates = mutableMapOf<String, Any?>()

                snapshot.child("asientos").children.forEach { aSnap ->
                    try {
                        val asiento = aSnap.value<Int>()
                        updates["viajes/$ruta/$fecha/$horario/ocupados/$asiento"] = null
                    } catch (e: Exception) {}
                }

                // Borrar verificaciones por reservaId (robusto): el código QR del webhook Stripe
                // usa un timestamp distinto al de la reserva, así que reconstruirlo no coincidía
                // y dejaba el QR vivo → boleto cancelado seguía escaneando válido al abordar.
                val verifSnap = db.reference("viajes/$ruta/$fecha/$horario/verificaciones").valueEvents.first()
                if (verifSnap.exists) {
                    verifSnap.children.forEach { vSnap ->
                        val rid = try { vSnap.child("reservaId").value<String>() } catch (e: Exception) { "" }
                        if (rid == idReserva) {
                            updates["viajes/$ruta/$fecha/$horario/verificaciones/${vSnap.key}"] = null
                        }
                    }
                }

                updates[pathReserva] = null

                val cancelId = db.reference("historial_cancelaciones").push().key ?: ""
                val pasajeros = mutableListOf<String>()
                // Snapshot estructurado para poder reasignar (recrear boleto + QR en otro viaje)
                val snapPasajeros = mutableListOf<Map<String, Any?>>()
                var correoTitular = ""
                snapshot.child("pasajeros").children.forEach { pSnap ->
                    try {
                        val p = PasajeroInfo.fromSnapshot(pSnap)
                        pasajeros.add("${p.nombre} (Asiento ${p.asiento})")
                        if (correoTitular.isBlank() && p.correo.isNotBlank()) correoTitular = p.correo
                        snapPasajeros.add(mapOf(
                            "nombre" to p.nombre,
                            "asiento" to p.asiento,
                            "telefono" to p.telefono,
                            "correo" to p.correo,
                            "parada" to p.parada,
                            "maletasExtras" to p.maletasExtras,
                            "llevaMascota" to p.llevaMascota
                        ))
                    } catch (e: Exception) {}
                }
                val totalPagado = try { snapshot.child("totalPagado").value<Int>() } catch (e: Exception) { 0 }
                val origenDestinoReserva = try { snapshot.child("origenDestino").value<String>() } catch (e: Exception) { ruta }
                val metodoPagoReserva = try { snapshot.child("metodoPago").value<String>() } catch (e: Exception) { "" }
                // Sin esto, al reasignar la cancelada no hay de dónde sacar el abordaje.
                val puntoSubidaReserva = try { snapshot.child("puntoSubida").value<String>() } catch (e: Exception) { "" }

                // Atribución de operador: quién canceló (el admin autenticado)
                val operadorUid = network.FirebaseClient.auth.currentUser?.uid ?: ""
                val operadorNombre = try {
                    val uSnap = db.reference("usuarios/$operadorUid").valueEvents.first()
                    val n = try { uSnap.child("nombre").value<String?>() } catch (e: Exception) { null }
                    val c = try { uSnap.child("correo").value<String?>() } catch (e: Exception) { null }
                    n?.takeIf { it.isNotBlank() }
                        ?: c?.takeIf { it.isNotBlank() }
                        ?: network.FirebaseClient.auth.currentUser?.email
                        ?: ""
                } catch (e: Exception) {
                    network.FirebaseClient.auth.currentUser?.email ?: ""
                }
                val ahoraCancel = org.example.abigail.obtenerMilisegundosActuales().toString()

                updates["historial_cancelaciones/$cancelId/ruta"] = ruta
                updates["historial_cancelaciones/$cancelId/fecha"] = fecha
                updates["historial_cancelaciones/$cancelId/horario"] = horario
                updates["historial_cancelaciones/$cancelId/reservaId"] = idReserva
                updates["historial_cancelaciones/$cancelId/pasajeros"] = pasajeros.joinToString(", ")
                updates["historial_cancelaciones/$cancelId/totalReembolsar"] = totalPagado
                updates["historial_cancelaciones/$cancelId/fechaCancelacion"] = ahoraCancel
                updates["historial_cancelaciones/$cancelId/canceladoPorUid"] = operadorUid
                updates["historial_cancelaciones/$cancelId/canceladoPorNombre"] = operadorNombre
                updates["historial_cancelaciones/$cancelId/origen"] = "reserva_individual"
                updates["historial_cancelaciones/$cancelId/estadoResolucion"] = "pendiente"
                updates["historial_cancelaciones/$cancelId/correo"] = correoTitular
                updates["historial_cancelaciones/$cancelId/snapPasajeros"] = snapPasajeros
                updates["historial_cancelaciones/$cancelId/snapTotalPagado"] = totalPagado
                updates["historial_cancelaciones/$cancelId/snapOrigenDestino"] = origenDestinoReserva
                updates["historial_cancelaciones/$cancelId/snapMetodoPago"] = metodoPagoReserva
                updates["historial_cancelaciones/$cancelId/snapPuntoSubida"] = puntoSubidaReserva

                // Bitácora de auditoría
                val audId = db.reference("auditoria").push().key ?: "aud_$ahoraCancel"
                updates["auditoria/$audId"] = mapOf(
                    "accion" to "reserva_cancelada",
                    "operadorUid" to operadorUid,
                    "operadorNombre" to operadorNombre,
                    "ruta" to ruta,
                    "fecha" to fecha,
                    "horario" to horario,
                    "reservaId" to idReserva,
                    "monto" to totalPagado,
                    "timestamp" to ahoraCancel
                )

                db.reference().updateChildren(updates)

                NotificacionViewModel.enviar(
                    tipo = "alerta",
                    titulo = "Boleto cancelado",
                    mensaje = "Se canceló la reserva de ${pasajeros.joinToString(", ")} en $ruta ($fecha $horario). Reembolso pendiente: $$totalPagado MXN",
                    destinatarioRol = "admin"
                )

                true
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "BookingRepositoryImpl.cancelarReservaAdmin[$idReserva]")
                false
            }
        }

        override suspend fun obtenerMapaAsientosAdmin(ruta: String, fecha: String, horario: String): List<AdminSeatInfo> {
            return try {
                val db = network.FirebaseClient.database
                val rutaBase = "viajes/$ruta/$fecha/$horario"

                // 1. Leer ocupados
                val ocupadosSnap = db.reference("$rutaBase/ocupados").valueEvents.first()
                val ocupados = mutableSetOf<Int>()
                if (ocupadosSnap.exists) {
                    ocupadosSnap.children.forEach { child ->
                        val asiento = child.key?.toIntOrNull()
                        if (asiento != null) {
                            val valor = try { child.value<Boolean>() } catch (e: Exception) { false }
                            if (valor) ocupados.add(asiento)
                        }
                    }
                }

                // 2. Leer bloqueos con pago_pendiente (congelados)
                val bloqueosSnap = db.reference("$rutaBase/bloqueos").valueEvents.first()
                val congelados = mutableMapOf<Int, Pair<String, Long>>() // asiento -> (paymentIntentId, expiraEn)
                val tiempoActual = Clock.System.now().toEpochMilliseconds()
                if (bloqueosSnap.exists) {
                    bloqueosSnap.children.forEach { asientoSnap ->
                        val asiento = asientoSnap.key?.toIntOrNull() ?: return@forEach
                        try {
                            val pagoPendiente = asientoSnap.child("pago_pendiente")
                            if (pagoPendiente.exists) {
                                val piId = try { pagoPendiente.child("paymentIntentId").value<String>() } catch (e: Exception) { "" }
                                val expiraEn = try { pagoPendiente.child("expiraEn").value<String>().toLongOrNull() ?: 0L } catch (e: Exception) { 0L }
                                if (expiraEn > tiempoActual) {
                                    congelados[asiento] = Pair(piId, expiraEn)
                                }
                            }
                        } catch (e: Exception) {}
                    }
                }

                // 3. Leer reservas para mapear asiento -> pasajero (solo pagados/confirmados)
                val asientoPasajeroMap = mutableMapOf<Int, PasajeroInfo>()
                val asientoMetodoPagoMap = mutableMapOf<Int, String>()
                val reservasSnap = db.reference("$rutaBase/reservas").valueEvents.first()
                if (reservasSnap.exists) {
                    reservasSnap.children.forEach { reservaSnap ->
                        try {
                            val metodoPago = try { reservaSnap.child("metodoPago").value<String>() } catch (e: Exception) { "" }
                            val asientosReales = try {
                                reservaSnap.child("asientos").children.mapNotNull { c ->
                                    try { c.value<Int>() } catch (e: Exception) { null }
                                }
                            } catch (e: Exception) { emptyList() }

                            val pasajerosReserva = mutableListOf<PasajeroInfo>()
                            try {
                                reservaSnap.child("pasajeros").children.forEach { pSnap ->
                                    try { pasajerosReserva.add(PasajeroInfo.fromSnapshot(pSnap)) } catch (e: Exception) {}
                                }
                            } catch (e: Exception) {}

                            asientosReales.forEachIndexed { index, numAsiento ->
                                val pasajero = pasajerosReserva.getOrNull(index)
                                    ?: PasajeroInfo(nombre = "Pasajero ${index + 1}", asiento = numAsiento)
                                asientoPasajeroMap[numAsiento] = pasajero
                                asientoMetodoPagoMap[numAsiento] = metodoPago
                            }
                        } catch (e: Exception) {}
                    }
                }

                // 4. Leer reservas_pendientes para pasajeros de asientos congelados
                val congeladoPasajeroMap = mutableMapOf<Int, PasajeroInfo>()
                val congeladoMetodoMap = mutableMapOf<Int, String>()
                congelados.forEach { (asiento, pair) ->
                    val piId = pair.first
                    if (piId.isNotBlank()) {
                        try {
                            val pendienteSnap = db.reference("reservas_pendientes/$piId").valueEvents.first()
                            if (pendienteSnap.exists) {
                                val metodo = try { pendienteSnap.child("metodoPago").value<String>() } catch (e: Exception) { "spei" }
                                val pasajeros = mutableListOf<PasajeroInfo>()
                                try {
                                    pendienteSnap.child("pasajeros").children.forEach { pSnap ->
                                        try { pasajeros.add(PasajeroInfo.fromSnapshot(pSnap)) } catch (e: Exception) {}
                                    }
                                } catch (e: Exception) {}
                                val asientosArr = try {
                                    pendienteSnap.child("asientosArray").children.mapNotNull { c ->
                                        try { c.value<Int>() } catch (e: Exception) { null }
                                    }
                                } catch (e: Exception) { emptyList() }

                                asientosArr.forEachIndexed { idx, numAsiento ->
                                    if (numAsiento == asiento) {
                                        congeladoPasajeroMap[asiento] = pasajeros.getOrNull(idx)
                                            ?: PasajeroInfo(nombre = "Pasajero ${idx + 1}", asiento = numAsiento)
                                        congeladoMetodoMap[asiento] = metodo
                                    }
                                }
                            }
                        } catch (e: Exception) {}
                    }
                }

                // 5. Determinar capacidad del vehículo (default 13 si no hay dato)
                val capacidadDefault = 13
                val totalAsientos = try {
                    val progSnap = db.reference("viajes_programados/$ruta/$fecha/$horario/capacidad").valueEvents.first()
                    if (progSnap.exists) progSnap.value<Int>() else capacidadDefault
                } catch (e: Exception) { capacidadDefault }

                // Construir lista de asientos sobre la numeración real del vehículo
                numerosDeAsiento(ruta, fecha, horario, totalAsientos).map { num ->
                    when {
                        num in ocupados -> AdminSeatInfo(
                            asiento = num,
                            status = AdminSeatStatus.PAGADO,
                            pasajero = asientoPasajeroMap[num],
                            metodoPago = asientoMetodoPagoMap[num] ?: ""
                        )
                        num in congelados -> AdminSeatInfo(
                            asiento = num,
                            status = AdminSeatStatus.CONGELADO,
                            pasajero = congeladoPasajeroMap[num],
                            metodoPago = congeladoMetodoMap[num] ?: "",
                            expiraEn = congelados[num]?.second ?: 0L,
                            paymentIntentId = congelados[num]?.first ?: ""
                        )
                        else -> AdminSeatInfo(asiento = num, status = AdminSeatStatus.LIBRE)
                    }
                }
            } catch (e: Exception) {
                (1..13).map { AdminSeatInfo(asiento = it, status = AdminSeatStatus.LIBRE) } // fallback en error
            }
        }

        override suspend fun obtenerHistorialPagos(): List<PagoRegistro> {
            return try {
                val db = network.FirebaseClient.database
                val resultados = mutableListOf<PagoRegistro>()

                // 1. Pagos confirmados
                val confirmadosSnap = db.reference("pagos_confirmados").valueEvents.first()
                if (confirmadosSnap.exists) {
                    confirmadosSnap.children.forEach { snap ->
                        try {
                            // Saltar espejos (tarjeta): pagos_confirmados/{sessionId} es solo para que
                            // el cliente detecte la confirmación; el pago real está bajo {pi_id}.
                            val esEspejo = try { snap.child("esEspejo").value<Boolean>() } catch (_: Exception) { false }
                            if (esEspejo) return@forEach
                            val pasajerosList = mutableListOf<PasajeroInfo>()
                            if (snap.child("pasajeros").exists) {
                                snap.child("pasajeros").children.forEach { pSnap ->
                                    try { pasajerosList.add(PasajeroInfo.fromSnapshot(pSnap)) } catch (_: Exception) {}
                                }
                            }
                            resultados.add(PagoRegistro(
                                paymentIntentId = snap.key ?: "",
                                ruta = try { snap.child("ruta").value<String>() } catch (e: Exception) { "" },
                                fecha = try { snap.child("fecha").value<String>() } catch (e: Exception) { "" },
                                horario = try { snap.child("horario").value<String>() } catch (e: Exception) { "" },
                                asientos = try { snap.child("asientos").value<String>() } catch (e: Exception) { "" },
                                monto = try { snap.child("monto").value<Int>() } catch (e: Exception) { try { snap.child("monto").value<Double>().toInt() } catch (e2: Exception) { 0 } },
                                timestamp = try { snap.child("timestamp").value<String>().toLongOrNull() ?: 0L } catch (e: Exception) { 0L },
                                status = try { snap.child("status").value<String>() } catch (e: Exception) { "" },
                                metodoPago = try { snap.child("metodoPago").value<String>() } catch (e: Exception) { "" },
                                email = try { snap.child("email").value<String>() } catch (e: Exception) { "" },
                                clabe = try { snap.child("clabe").value<String>() } catch (e: Exception) { "" },
                                oxxoNumber = try { snap.child("oxxoNumber").value<String>() } catch (e: Exception) { "" },
                                requiereAtencion = try { snap.child("requiereAtencion").value<Boolean>() } catch (e: Exception) { false },
                                motivo = try { snap.child("motivo").value<String>() } catch (e: Exception) { "" },
                                pasajeros = pasajerosList,
                                reservaId = try { snap.child("reservaId").value<String>() } catch (e: Exception) { "" },
                            ))
                        } catch (_: Exception) {}
                    }
                }

                // 2. Pagos pendientes (que aún no han sido confirmados)
                val pendientesSnap = db.reference("reservas_pendientes").valueEvents.first()
                if (pendientesSnap.exists) {
                    pendientesSnap.children.forEach { snap ->
                        try {
                            val statusVal = try { snap.child("status").value<String>() } catch (e: Exception) { "" }
                            // Solo agregar los que NO están en pagos_confirmados ya
                            val piId = snap.key ?: ""
                            if (resultados.none { it.paymentIntentId == piId }) {
                                val pasajerosList = mutableListOf<PasajeroInfo>()
                                if (snap.child("pasajeros").exists) {
                                    snap.child("pasajeros").children.forEach { pSnap ->
                                        try { pasajerosList.add(PasajeroInfo.fromSnapshot(pSnap)) } catch (_: Exception) {}
                                    }
                                }
                                resultados.add(PagoRegistro(
                                    paymentIntentId = piId,
                                    ruta = try { snap.child("ruta").value<String>() } catch (e: Exception) { "" },
                                    fecha = try { snap.child("fecha").value<String>() } catch (e: Exception) { "" },
                                    horario = try { snap.child("horario").value<String>() } catch (e: Exception) { "" },
                                    asientos = try { snap.child("asientos").value<String>() } catch (e: Exception) { "" },
                                    monto = try { snap.child("totalPagado").value<Int>() } catch (e: Exception) { 0 },
                                    timestamp = try { snap.child("timestamp").value<String>().toLongOrNull() ?: 0L } catch (e: Exception) { 0L },
                                    status = statusVal,
                                    metodoPago = try { snap.child("metodoPago").value<String>() } catch (e: Exception) { "" },
                                    email = try { snap.child("email").value<String>() } catch (e: Exception) { "" },
                                    clabe = try { snap.child("clabe").value<String>() } catch (e: Exception) { "" },
                                    oxxoNumber = try { snap.child("oxxoNumber").value<String>() } catch (e: Exception) { "" },
                                    voucherUrl = try { snap.child("voucherUrl").value<String>() } catch (e: Exception) { "" },
                                    pasajeros = pasajerosList,
                                ))
                            }
                        } catch (_: Exception) {}
                    }
                }

                // Ordenar por timestamp descendente (más recientes primero)
                // Limitar a los últimos 200 registros para no saturar memoria en pantalla admin
                resultados.sortByDescending { it.timestamp }
                resultados.take(200)
            } catch (e: Exception) {
                emptyList()
            }
        }

        override suspend fun obtenerConflictosAsientos(): List<ConflictoAsiento> {
            return try {
                val db = network.FirebaseClient.database
                val snap = db.reference("conflictos_asientos").valueEvents.first()
                if (!snap.exists) return emptyList()

                val resultados = mutableListOf<ConflictoAsiento>()
                snap.children.forEach { cs ->
                    try {
                        val pasConflicto = mutableListOf<PasajeroInfo>()
                        if (cs.child("pasajerosEnConflicto").exists) {
                            cs.child("pasajerosEnConflicto").children.forEach { p ->
                                try { pasConflicto.add(PasajeroInfo.fromSnapshot(p)) } catch (_: Exception) {}
                            }
                        }
                        val asConflicto = mutableListOf<Int>()
                        if (cs.child("asientosEnConflicto").exists) {
                            cs.child("asientosEnConflicto").children.forEach { a ->
                                try { asConflicto.add(a.value<Int>()) } catch (_: Exception) {}
                            }
                        }
                        val asAsignados = mutableListOf<Int>()
                        if (cs.child("asientosAsignados").exists) {
                            cs.child("asientosAsignados").children.forEach { a ->
                                try { asAsignados.add(a.value<Int>()) } catch (_: Exception) {}
                            }
                        }
                        resultados.add(ConflictoAsiento(
                            id = cs.key ?: "",
                            paymentIntentId = try { cs.child("paymentIntentId").value<String>() } catch (e: Exception) { "" },
                            ruta = try { cs.child("ruta").value<String>() } catch (e: Exception) { "" },
                            fecha = try { cs.child("fecha").value<String>() } catch (e: Exception) { "" },
                            horario = try { cs.child("horario").value<String>() } catch (e: Exception) { "" },
                            metodoPago = try { cs.child("metodoPago").value<String>() } catch (e: Exception) { "" },
                            email = try { cs.child("email").value<String>() } catch (e: Exception) { "" },
                            montoTotal = try { cs.child("montoTotal").value<Int>() } catch (e: Exception) { 0 },
                            montoPendiente = try { cs.child("montoPendiente").value<Int>() } catch (e: Exception) { 0 },
                            asientosAsignados = asAsignados,
                            asientosEnConflicto = asConflicto,
                            pasajerosEnConflicto = pasConflicto,
                            status = try { cs.child("status").value<String>() } catch (e: Exception) { "" },
                            timestamp = try { cs.child("timestamp").value<String>().toLongOrNull() ?: 0L } catch (e: Exception) { 0L },
                            reservaIdParcial = try { cs.child("reservaIdParcial").value<String>() } catch (e: Exception) { "" },
                        ))
                    } catch (_: Exception) {}
                }
                resultados.sortByDescending { it.timestamp }
                resultados.filter { it.status == "pendiente" }
            } catch (e: Exception) {
                emptyList()
            }
        }

        override suspend fun reasignarAsientoConflicto(conflictoId: String, asientoViejo: Int, asientoNuevo: Int, pasajero: PasajeroInfo): Boolean {
            return try {
                val db = network.FirebaseClient.database
                val conflictoSnap = db.reference("conflictos_asientos/$conflictoId").valueEvents.first()
                if (!conflictoSnap.exists) return false

                val ruta = try { conflictoSnap.child("ruta").value<String>() } catch (e: Exception) { return false }
                val fecha = try { conflictoSnap.child("fecha").value<String>() } catch (e: Exception) { return false }
                val horario = try { conflictoSnap.child("horario").value<String>() } catch (e: Exception) { return false }
                val rutaBase = "viajes/$ruta/$fecha/$horario"

                // Verificar que el nuevo asiento esté libre
                val ocupadoSnap = db.reference("$rutaBase/ocupados/$asientoNuevo").valueEvents.first()
                if (ocupadoSnap.exists && try { ocupadoSnap.value<Boolean>() } catch (e: Exception) { false }) return false

                val reservaIdParcial = try { conflictoSnap.child("reservaIdParcial").value<String>() } catch (e: Exception) { "" }
                val piId = try { conflictoSnap.child("paymentIntentId").value<String>() } catch (e: Exception) { "" }
                val metodo = try { conflictoSnap.child("metodoPago").value<String>() } catch (e: Exception) { "" }
                val montoPendiente = try { conflictoSnap.child("montoPendiente").value<Int>() } catch (e: Exception) { 0 }

                val asientosConflicto = mutableListOf<Int>()
                if (conflictoSnap.child("asientosEnConflicto").exists) {
                    conflictoSnap.child("asientosEnConflicto").children.forEach { a ->
                        try { asientosConflicto.add(a.value<Int>()) } catch (_: Exception) {}
                    }
                }
                val pasajerosConflicto = mutableListOf<PasajeroInfo>()
                if (conflictoSnap.child("pasajerosEnConflicto").exists) {
                    conflictoSnap.child("pasajerosEnConflicto").children.forEach { p ->
                        try { pasajerosConflicto.add(PasajeroInfo.fromSnapshot(p)) } catch (_: Exception) {}
                    }
                }

                val pasajeroActualizado = pasajero.copy(asiento = asientoNuevo)
                val precioPorAsiento = CalculoReprogramacion.precioPorAsientoConflicto(montoPendiente, asientosConflicto.size)

                // Crear reserva para este asiento reasignado
                val nuevaReservaId = db.reference("$rutaBase/reservas").push().key ?: return false
                val tsReasignacion = Clock.System.now().toEpochMilliseconds().toString()

                // Escribir todo en un solo updateChildren atómico
                val updates = mutableMapOf<String, Any?>()
                updates["$rutaBase/ocupados/$asientoNuevo"] = true
                updates["$rutaBase/reservas/$nuevaReservaId"] = mapOf(
                    "asientos" to listOf(asientoNuevo),
                    "pasajeros" to listOf(mapOf(
                        "asiento" to asientoNuevo,
                        "nombre" to pasajeroActualizado.nombre,
                        "telefono" to pasajeroActualizado.telefono,
                        "correo" to pasajeroActualizado.correo,
                        "parada" to pasajeroActualizado.parada,
                        "maletasExtras" to pasajeroActualizado.maletasExtras,
                        "llevaMascota" to pasajeroActualizado.llevaMascota,
                    )),
                    "totalPagado" to precioPorAsiento,
                    "fechaViaje" to fecha,
                    "horario" to horario,
                    "origenDestino" to ruta,
                    "timestamp" to tsReasignacion,
                    "metodoPago" to metodo,
                    "paymentIntentId" to piId,
                    // Sin esto la reserva del asiento reasignado nacía "Sin asignar".
                    "puntoSubida" to puntoSubidaDeViaje(
                        ruta, fecha, horario,
                        try { conflictoSnap.child("puntoSubida").value<String>() } catch (e: Exception) { "" }
                    ),
                    "reasignado" to true,
                )

                // Crear verificación QR para que el pasajero reasignado pueda abordar
                // (admin puede reenviar el boleto con este código vía "reenviar boletos").
                val codigoQRReasignado = "SMARTVAN-$tsReasignacion-$asientoNuevo"
                updates["$rutaBase/verificaciones/$codigoQRReasignado"] = mapOf(
                    "asiento" to asientoNuevo,
                    "pasajero" to pasajeroActualizado.nombre,
                    "verificado" to false,
                    "reservaId" to nuevaReservaId,
                )

                // Actualizar conflicto: remover asiento Y pasajero del conflicto
                val nuevosAsientosConflicto = asientosConflicto.filter { it != asientoViejo }
                val nuevosPasajerosConflicto = pasajerosConflicto.filterIndexed { idx, _ ->
                    asientosConflicto.getOrNull(idx) != asientoViejo
                }

                if (nuevosAsientosConflicto.isEmpty()) {
                    updates["conflictos_asientos/$conflictoId/status"] = "reasignado"
                    updates["conflictos_asientos/$conflictoId/asientosEnConflicto"] = emptyList<Int>()
                    updates["conflictos_asientos/$conflictoId/pasajerosEnConflicto"] = emptyList<Any>()
                } else {
                    updates["conflictos_asientos/$conflictoId/asientosEnConflicto"] = nuevosAsientosConflicto
                    updates["conflictos_asientos/$conflictoId/pasajerosEnConflicto"] = nuevosPasajerosConflicto.map { p ->
                        mapOf(
                            "asiento" to p.asiento, "nombre" to p.nombre,
                            "telefono" to p.telefono, "correo" to p.correo,
                            "parada" to p.parada, "maletasExtras" to p.maletasExtras,
                            "llevaMascota" to p.llevaMascota,
                        )
                    }
                }

                db.reference().updateChildren(updates)
                true
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "BookingRepositoryImpl.reasignarAsientoConflicto[$conflictoId]")
                false
            }
        }

        override suspend fun marcarConflictoReembolsado(conflictoId: String): Boolean {
            return try {
                val db = network.FirebaseClient.database
                db.reference("conflictos_asientos/$conflictoId/status").setValue("reembolsado")
                true
            } catch (e: Exception) {
                false
            }
        }

        // ═══ Vehículos con layout de asientos ═══

        override suspend fun guardarVehiculoConfig(vehiculo: VehiculoConfig): Boolean {
            return try {
                val db = network.FirebaseClient.database
                val id = vehiculo.id.ifBlank { db.reference("vehiculos_config").push().key ?: return false }
                val filasData = vehiculo.filas.map { fila ->
                    mapOf("asientos" to fila.asientos)
                }
                db.reference("vehiculos_config/$id").setValue(mapOf(
                    "nombre" to vehiculo.nombre,
                    "marca" to vehiculo.marca,
                    "modelo" to vehiculo.modelo,
                    "placas" to vehiculo.placas,
                    "capacidad" to vehiculo.capacidad,
                    "columnas" to vehiculo.columnas,
                    "filas" to filasData,
                ))
                // También agregar el nombre al catálogo plano de vehiculos: así aparece en el
                // dropdown de "Vehículo Asignado" al programar, y el nombre COINCIDE exacto con
                // vehiculos_config → el viaje vincula vehiculoId+capacidad (si no, caía a 13).
                try {
                    val catRef = db.reference("catalogos/vehiculos")
                    val snap = catRef.valueEvents.first()
                    val lista = if (snap.exists) (snap.value<List<String>?>() ?: emptyList()) else emptyList()
                    if (!lista.contains(vehiculo.nombre)) {
                        catRef.setValue(lista + vehiculo.nombre)
                    }
                } catch (e: Exception) { /* best effort */ }
                true
            } catch (e: Exception) {
                false
            }
        }

        override suspend fun editarVehiculoConfig(nombreViejo: String, vehiculo: VehiculoConfig): Boolean {
            return try {
                val db = network.FirebaseClient.database
                val id = vehiculo.id.ifBlank { return false } // editar requiere id existente
                val filasData = vehiculo.filas.map { fila -> mapOf("asientos" to fila.asientos) }
                db.reference("vehiculos_config/$id").setValue(mapOf(
                    "nombre" to vehiculo.nombre,
                    "marca" to vehiculo.marca,
                    "modelo" to vehiculo.modelo,
                    "placas" to vehiculo.placas,
                    "capacidad" to vehiculo.capacidad,
                    "columnas" to vehiculo.columnas,
                    "filas" to filasData,
                ))

                // Catálogo plano: reemplazar el nombre viejo por el nuevo (para el dropdown de programar)
                try {
                    val catRef = db.reference("catalogos/vehiculos")
                    val snap = catRef.valueEvents.first()
                    val lista = if (snap.exists) (snap.value<List<String>?>() ?: emptyList()) else emptyList()
                    var nuevaLista = lista.map { if (it == nombreViejo) vehiculo.nombre else it }
                    if (!nuevaLista.contains(vehiculo.nombre)) nuevaLista = nuevaLista + vehiculo.nombre
                    catRef.setValue(nuevaLista.distinct())
                } catch (e: Exception) { /* best effort */ }

                // Propagar a viajes_programados futuros que usan este vehículo: nombre, id y capacidad
                try {
                    val hoy = org.example.abigail.obtenerFechaHoy()
                    val vpSnap = db.reference("viajes_programados").valueEvents.first()
                    if (vpSnap.exists) {
                        val updates = mutableMapOf<String, Any?>()
                        vpSnap.children.forEach { rutaSnap ->
                            val ruta = rutaSnap.key ?: return@forEach
                            rutaSnap.children.forEach { fechaSnap ->
                                val fecha = fechaSnap.key ?: return@forEach
                                if (fecha < hoy) return@forEach
                                fechaSnap.children.forEach { hSnap ->
                                    val horario = hSnap.key ?: return@forEach
                                    val veh = try { hSnap.child("vehiculo").value<String>() } catch (e: Exception) { "" }
                                    if (veh == nombreViejo) {
                                        val base = "viajes_programados/$ruta/$fecha/$horario"
                                        updates["$base/vehiculo"] = vehiculo.nombre
                                        updates["$base/vehiculoId"] = id
                                        updates["$base/capacidad"] = vehiculo.capacidad
                                    }
                                }
                            }
                        }
                        if (updates.isNotEmpty()) db.reference().updateChildren(updates)
                    }
                } catch (e: Exception) { /* best effort */ }
                true
            } catch (e: Exception) {
                network.CrashReporter.recordException(e, "BookingRepositoryImpl.editarVehiculoConfig")
                false
            }
        }

        override suspend fun obtenerVehiculosConfig(): List<VehiculoConfig> {
            return try {
                val db = network.FirebaseClient.database
                val snap = db.reference("vehiculos_config").valueEvents.first()
                if (!snap.exists) return emptyList()
                val resultados = mutableListOf<VehiculoConfig>()
                snap.children.forEach { vs ->
                    try {
                        val filas = mutableListOf<FilaVehiculo>()
                        if (vs.child("filas").exists) {
                            vs.child("filas").children.forEach { fSnap ->
                                val asientos = mutableListOf<Int>()
                                if (fSnap.child("asientos").exists) {
                                    fSnap.child("asientos").children.forEach { a ->
                                        try { asientos.add(a.value<Int>()) } catch (_: Exception) { asientos.add(0) }
                                    }
                                }
                                filas.add(FilaVehiculo(asientos = asientos))
                            }
                        }
                        resultados.add(VehiculoConfig(
                            id = vs.key ?: "",
                            nombre = try { vs.child("nombre").value<String>() } catch (e: Exception) { "" },
                            marca = try { vs.child("marca").value<String>() } catch (e: Exception) { "" },
                            modelo = try { vs.child("modelo").value<String>() } catch (e: Exception) { "" },
                            placas = try { vs.child("placas").value<String>() } catch (e: Exception) { "" },
                            capacidad = try { vs.child("capacidad").value<Int>() } catch (e: Exception) { 13 },
                            columnas = try { vs.child("columnas").value<Int>() } catch (e: Exception) { 4 },
                            filas = filas,
                        ))
                    } catch (_: Exception) {}
                }
                resultados
            } catch (e: Exception) {
                emptyList()
            }
        }

        override suspend fun obtenerVehiculoConfig(vehiculoId: String): VehiculoConfig? {
            return try {
                val db = network.FirebaseClient.database
                val vs = db.reference("vehiculos_config/$vehiculoId").valueEvents.first()
                if (!vs.exists) return null
                val filas = mutableListOf<FilaVehiculo>()
                if (vs.child("filas").exists) {
                    vs.child("filas").children.forEach { fSnap ->
                        val asientos = mutableListOf<Int>()
                        if (fSnap.child("asientos").exists) {
                            fSnap.child("asientos").children.forEach { a ->
                                try { asientos.add(a.value<Int>()) } catch (_: Exception) { asientos.add(0) }
                            }
                        }
                        filas.add(FilaVehiculo(asientos = asientos))
                    }
                }
                VehiculoConfig(
                    id = vehiculoId,
                    nombre = try { vs.child("nombre").value<String>() } catch (e: Exception) { "" },
                    marca = try { vs.child("marca").value<String>() } catch (e: Exception) { "" },
                    modelo = try { vs.child("modelo").value<String>() } catch (e: Exception) { "" },
                    placas = try { vs.child("placas").value<String>() } catch (e: Exception) { "" },
                    capacidad = try { vs.child("capacidad").value<Int>() } catch (e: Exception) { 13 },
                    columnas = try { vs.child("columnas").value<Int>() } catch (e: Exception) { 4 },
                    filas = filas,
                )
            } catch (e: Exception) {
                null
            }
        }

        override suspend fun eliminarVehiculoConfig(vehiculoId: String): Boolean {
            return try {
                val db = network.FirebaseClient.database
                // Leer el nombre antes de borrar, para quitarlo también del catálogo plano
                val nombre = try {
                    val s = db.reference("vehiculos_config/$vehiculoId/nombre").valueEvents.first()
                    if (s.exists) s.value<String>() else ""
                } catch (e: Exception) { "" }
                db.reference("vehiculos_config/$vehiculoId").removeValue()
                if (nombre.isNotBlank()) {
                    try {
                        val catRef = db.reference("catalogos/vehiculos")
                        val snap = catRef.valueEvents.first()
                        if (snap.exists) {
                            val lista = (snap.value<List<String>?>() ?: emptyList()).filter { it != nombre }
                            catRef.setValue(lista)
                        }
                    } catch (e: Exception) { /* best effort */ }
                }
                true
            } catch (e: Exception) {
                false
            }
        }

        override suspend fun obtenerLayoutDeViaje(ruta: String, fecha: String, horario: String): VehiculoConfig? {
            return try {
                val db = network.FirebaseClient.database
                val base = "viajes_programados/$ruta/$fecha/$horario"
                // 1º intentar el vehiculoId — trae filas + capacidad reales del vehículo asignado.
                val vehiculoIdSnap = db.reference("$base/vehiculoId").valueEvents.first()
                val vehiculoId = if (vehiculoIdSnap.exists) try { vehiculoIdSnap.value<String>() } catch (_: Exception) { "" } else ""
                if (vehiculoId.isNotBlank()) {
                    val config = obtenerVehiculoConfig(vehiculoId)
                    // Un vehículo con capacidad pero sin layout configurado devuelve filas vacías;
                    // se generan desde la capacidad para no renderizar cero asientos.
                    if (config != null) {
                        return if (config.filas.isEmpty() && config.capacidad > 0)
                            generarLayoutGenerico(config.capacidad).copy(nombre = config.nombre)
                        else config
                    }
                }
                // 2º fallback: si el viaje no tiene vehiculoId (viajes viejos) o su config fue
                // borrada, tomar la capacidad guardada en el viaje y GENERAR las filas aquí.
                // Antes se devolvía VehiculoConfig(capacidad) sin filas; las pantallas que hacen
                // filas.forEach directo (selección de asientos del cliente) renderizaban CERO.
                val capSnap = db.reference("$base/capacidad").valueEvents.first()
                val cap = try { capSnap.value<Int>() } catch (_: Exception) { 0 }
                if (cap > 0) generarLayoutGenerico(cap) else null
            } catch (e: Exception) {
                null
            }
        }

    }

